import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

/* ─────────────────────────────────────────
   FONTS injected once
───────────────────────────────────────── */
const FONT_LINK = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;1,300;1,400&family=DM+Sans:wght@300;400;500&display=swap');
`;

/* ─────────────────────────────────────────
   GLOBAL CSS
───────────────────────────────────────── */
const GLOBAL_CSS = `
${FONT_LINK}

*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

:root {
  --black: #080808;
  --dark: #0E0E0E;
  --card: #141414;
  --border: rgba(255,255,255,0.07);
  --gold: #D4AF37;
  --gold2: #F0D060;
  --gold3: #C9A227;
  --gold-dim: rgba(212,175,55,0.18);
  --white: #FFFFFF;
  --muted: rgba(255,255,255,0.45);
  --muted2: rgba(255,255,255,0.25);
  --serif: 'Cormorant Garamond', Georgia, serif;
  --sans: 'DM Sans', sans-serif;
}

html { scroll-behavior: smooth; }

body {
  background: var(--black);
  color: var(--white);
  font-family: var(--sans);
  overflow-x: hidden;
}

/* ── scrollbar ── */
::-webkit-scrollbar { width: 4px; }
::-webkit-scrollbar-track { background: var(--black); }
::-webkit-scrollbar-thumb { background: rgba(212,175,55,0.3); border-radius: 2px; }

/* ── NAV ── */
.lp-nav {
  position: fixed; top: 0; left: 0; right: 0; z-index: 200;
  padding: 1.5rem 3rem;
  transition: all 0.4s;
}
.lp-nav.scrolled {
  background: rgba(8,8,8,0.9);
  backdrop-filter: blur(20px);
  border-bottom: 0.5px solid rgba(212,175,55,0.12);
  padding: 1rem 3rem;
}
.lp-nav-inner {
  max-width: 1180px; margin: 0 auto;
  display: flex; align-items: center; justify-content: space-between;
}
.lp-logo {
  display: flex; align-items: center; gap: 10px;
  font-family: var(--sans); font-size: 15px; font-weight: 500;
  color: var(--white); text-decoration: none; letter-spacing: 0.2px;
}
.lp-logo-icon { width: 34px; height: 34px; flex-shrink: 0; }
.lp-logo em { color: var(--gold); font-style: normal; font-weight: 600; }
.lp-nav-links {
  display: flex; align-items: center; gap: 2.5rem;
}
.lp-nav-link {
  font-size: 13px; font-weight: 400; color: var(--muted);
  text-decoration: none; letter-spacing: 0.3px;
  transition: color 0.2s;
}
.lp-nav-link:hover { color: var(--white); }
.lp-nav-cta {
  padding: 10px 22px;
  background: linear-gradient(135deg, var(--gold), var(--gold2), var(--gold3));
  color: #0A0A0A; border: none; border-radius: 10px;
  font-family: var(--sans); font-size: 13px; font-weight: 500;
  letter-spacing: 0.8px; text-transform: uppercase;
  cursor: pointer; position: relative; overflow: hidden;
  transition: transform 0.15s, opacity 0.15s;
}
.lp-nav-cta::before {
  content: ''; position: absolute; inset: 0;
  background: linear-gradient(135deg, rgba(255,255,255,0.2), transparent 60%);
  pointer-events: none;
}
.lp-nav-cta:hover { transform: translateY(-1px); opacity: 0.92; }

/* ── HERO ── */
.lp-hero {
  min-height: 100vh;
  display: grid;
  grid-template-columns: 1fr 1fr;
  align-items: center;
  gap: 4rem;
  max-width: 1180px; margin: 0 auto;
  padding: 10rem 3rem 6rem;
  position: relative;
}

.lp-hero-left { position: relative; z-index: 2; }

.lp-hero-eyebrow {
  display: inline-flex; align-items: center; gap: 8px;
  padding: 6px 14px;
  background: rgba(212,175,55,0.1);
  border: 0.5px solid rgba(212,175,55,0.3);
  border-radius: 100px;
  font-size: 11px; font-weight: 500; color: var(--gold);
  letter-spacing: 1.5px; text-transform: uppercase;
  margin-bottom: 2rem;
}
.lp-hero-eyebrow-dot {
  width: 5px; height: 5px; border-radius: 50%;
  background: var(--gold);
  animation: pulse-gold 2s ease-in-out infinite;
}
@keyframes pulse-gold {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.4; transform: scale(0.7); }
}

.lp-hero-title {
  font-family: var(--serif);
  font-size: clamp(3rem, 5.5vw, 5.2rem);
  font-weight: 300; line-height: 1.08;
  letter-spacing: -1px;
  color: var(--white);
  margin-bottom: 1.75rem;
}
.lp-hero-title em {
  font-style: italic; font-weight: 300;
  color: var(--gold);
}

.lp-hero-sub {
  font-size: 16px; font-weight: 300;
  color: var(--muted); line-height: 1.8;
  max-width: 440px; margin-bottom: 3rem;
}

.lp-hero-ctas { display: flex; gap: 1rem; flex-wrap: wrap; }

.lp-btn-gold {
  padding: 16px 32px;
  background: linear-gradient(135deg, var(--gold), var(--gold2), var(--gold3));
  color: #0A0A0A; border: none; border-radius: 12px;
  font-family: var(--sans); font-size: 13px; font-weight: 500;
  letter-spacing: 1px; text-transform: uppercase;
  cursor: pointer; position: relative; overflow: hidden;
  transition: transform 0.2s, opacity 0.2s;
  display: flex; align-items: center; gap: 8px;
}
.lp-btn-gold::before {
  content: ''; position: absolute; inset: 0;
  background: linear-gradient(135deg, rgba(255,255,255,0.25), transparent 60%);
  pointer-events: none;
}
.lp-btn-gold:hover { transform: translateY(-2px); opacity: 0.92; }

.lp-btn-ghost {
  padding: 16px 28px;
  background: transparent;
  color: var(--muted); border: 0.5px solid rgba(255,255,255,0.15);
  border-radius: 12px;
  font-family: var(--sans); font-size: 13px; font-weight: 400;
  letter-spacing: 0.5px; cursor: pointer; text-decoration: none;
  display: inline-flex; align-items: center; gap: 8px;
  transition: border-color 0.2s, color 0.2s;
}
.lp-btn-ghost:hover { border-color: rgba(212,175,55,0.3); color: var(--white); }

/* ── HERO STATS STRIP ── */
.lp-hero-stats {
  display: flex; gap: 2.5rem; margin-top: 3.5rem;
  padding-top: 2.5rem;
  border-top: 0.5px solid var(--border);
}
.lp-stat-num {
  font-family: var(--serif); font-size: 2rem; font-weight: 300;
  color: var(--gold); line-height: 1;
}
.lp-stat-label {
  font-size: 11px; font-weight: 400; color: var(--muted2);
  letter-spacing: 0.5px; margin-top: 4px;
}

/* ── HERO RIGHT — QR SCAN ANIMATION ── */
.lp-hero-right {
  position: relative; display: flex;
  align-items: center; justify-content: center;
  z-index: 2;
}

/* Scene wrapper */
.scan-scene {
  position: relative;
  width: 380px; height: 480px;
}

/* Phone A (scanner) */
.phone-scanner {
  position: absolute;
  left: 0; top: 60px;
  width: 155px; height: 270px;
  background: #1A1A1A;
  border-radius: 22px;
  border: 1px solid rgba(255,255,255,0.1);
  overflow: hidden;
  box-shadow: 0 24px 60px rgba(0,0,0,0.6);
  z-index: 3;
  animation: phone-a-intro 0.8s cubic-bezier(0.34,1.56,0.64,1) both;
  animation-delay: 0.3s;
}
@keyframes phone-a-intro {
  from { opacity: 0; transform: translateX(-30px) rotate(-6deg); }
  to   { opacity: 1; transform: translateX(0) rotate(-6deg); }
}

.phone-scanner-screen {
  width: 100%; height: 100%;
  background: #111;
  display: flex; flex-direction: column;
  align-items: center; justify-content: center; gap: 12px;
  padding: 12px;
}
.scanner-viewfinder {
  width: 100px; height: 100px;
  border-radius: 10px;
  position: relative;
  background: rgba(212,175,55,0.04);
}
/* corner brackets of viewfinder */
.scanner-viewfinder::before,
.scanner-viewfinder::after {
  content: '';
  position: absolute;
  width: 20px; height: 20px;
  border-color: var(--gold); border-style: solid;
}
.scanner-viewfinder::before {
  top: 0; left: 0;
  border-width: 2px 0 0 2px;
  border-radius: 6px 0 0 0;
}
.scanner-viewfinder::after {
  bottom: 0; right: 0;
  border-width: 0 2px 2px 0;
  border-radius: 0 0 6px 0;
}
.vf-corner-tr {
  position: absolute; top: 0; right: 0;
  width: 20px; height: 20px;
  border-top: 2px solid var(--gold);
  border-right: 2px solid var(--gold);
  border-radius: 0 6px 0 0;
}
.vf-corner-bl {
  position: absolute; bottom: 0; left: 0;
  width: 20px; height: 20px;
  border-bottom: 2px solid var(--gold);
  border-left: 2px solid var(--gold);
  border-radius: 0 0 0 6px;
}

/* scanning laser line */
.scan-laser {
  position: absolute;
  left: 4px; right: 4px; height: 2px;
  background: linear-gradient(90deg, transparent, var(--gold), transparent);
  border-radius: 1px;
  top: 4px;
  animation: laser-sweep 2s ease-in-out infinite;
  animation-delay: 1.2s;
}
@keyframes laser-sweep {
  0%   { top: 8px; opacity: 0; }
  10%  { opacity: 1; }
  90%  { opacity: 1; }
  100% { top: 88px; opacity: 0; }
}

.scanner-label {
  font-size: 9px; font-weight: 400; color: var(--muted2);
  letter-spacing: 1px; text-align: center;
  text-transform: uppercase;
}

/* Mini QR inside viewfinder */
.mini-qr {
  position: absolute; inset: 10px;
  display: grid; grid-template-columns: repeat(6,1fr); grid-template-rows: repeat(6,1fr);
  gap: 1.5px; opacity: 0.6;
}
.mini-qr-cell { border-radius: 1px; }

/* Phone B (profile card) */
.phone-profile {
  position: absolute;
  right: 0; top: 0;
  width: 175px; height: 310px;
  background: #0E0E0E;
  border-radius: 24px;
  border: 1px solid rgba(212,175,55,0.2);
  overflow: hidden;
  box-shadow: 0 32px 80px rgba(0,0,0,0.7), 0 0 0 1px rgba(212,175,55,0.08);
  z-index: 4;
  animation: phone-b-intro 0.8s cubic-bezier(0.34,1.56,0.64,1) both;
  animation-delay: 0.5s;
}
@keyframes phone-b-intro {
  from { opacity: 0; transform: translateY(30px) rotate(4deg); }
  to   { opacity: 1; transform: translateY(0) rotate(4deg); }
}

/* ── card content opening animation ── */
.profile-card-wrap {
  width: 100%; height: 100%;
  animation: card-reveal 0.7s cubic-bezier(0.22,1,0.36,1) both;
  animation-delay: 2s;
}
@keyframes card-reveal {
  from { transform: translateY(40px); opacity: 0; }
  to   { transform: translateY(0); opacity: 1; }
}

.profile-banner {
  width: 100%; height: 110px;
  background: linear-gradient(160deg, #1a1a1a, #2a2020);
  position: relative; overflow: hidden;
}
.profile-banner-img {
  width: 100%; height: 100%;
  background: linear-gradient(160deg, #1e1a10 0%, #2a2010 50%, #181818 100%);
}
.profile-banner-fade {
  position: absolute; bottom: 0; left: 0; right: 0; height: 50px;
  background: linear-gradient(transparent, #0E0E0E);
}

/* floating avatar */
.profile-avatar-ring {
  position: absolute;
  bottom: -20px; left: 14px;
  width: 44px; height: 44px;
  border-radius: 50%;
  border: 2px solid #0E0E0E;
  background: #222;
  overflow: hidden;
  box-shadow: 0 0 0 1.5px rgba(212,175,55,0.5);
  animation: avatar-pop 0.4s cubic-bezier(0.34,1.56,0.64,1) both;
  animation-delay: 2.4s;
}
@keyframes avatar-pop {
  from { transform: scale(0.3); opacity: 0; }
  to   { transform: scale(1); opacity: 1; }
}
.profile-avatar-inner {
  width: 100%; height: 100%;
  background: linear-gradient(145deg, #2a2010, #3a3020);
  display: flex; align-items: center; justify-content: center;
  font-family: var(--serif); font-size: 14px; font-weight: 300;
  color: rgba(212,175,55,0.8); letter-spacing: 1px;
}

.profile-info {
  padding: 28px 14px 12px;
}
.profile-info-name {
  font-family: var(--serif); font-size: 14px; font-weight: 300;
  color: var(--white); line-height: 1.2; margin-bottom: 2px;
}
.profile-info-name em { font-style: italic; color: var(--gold); }
.profile-info-role {
  font-size: 8px; font-weight: 400; color: var(--muted2);
  letter-spacing: 0.3px; margin-bottom: 10px;
}

.profile-tiles {
  display: grid; grid-template-columns: 1fr 1fr 1fr;
  gap: 5px; margin-bottom: 8px;
}
.profile-tile {
  background: #1A1A1A;
  border: 0.5px solid rgba(255,255,255,0.06);
  border-radius: 8px; padding: 7px 6px;
  display: flex; flex-direction: column; gap: 4px;
  animation: tile-slide 0.3s ease both;
}
.profile-tile:nth-child(1) { animation-delay: 2.5s; }
.profile-tile:nth-child(2) { animation-delay: 2.65s; }
.profile-tile:nth-child(3) { animation-delay: 2.8s; }
@keyframes tile-slide {
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
}
.tile-dot {
  width: 12px; height: 12px; border-radius: 50%;
  background: rgba(212,175,55,0.15);
  display: flex; align-items: center; justify-content: center;
}
.tile-dot-inner { width: 5px; height: 5px; border-radius: 50%; background: var(--gold); }
.tile-text { font-size: 7px; color: rgba(255,255,255,0.35); line-height: 1.3; }

.profile-save-btn {
  width: 100%; padding: 8px;
  background: linear-gradient(135deg, var(--gold), var(--gold2));
  color: #0A0A0A; border: none; border-radius: 8px;
  font-size: 8px; font-weight: 500; letter-spacing: 0.8px;
  text-transform: uppercase; cursor: default;
  display: flex; align-items: center; justify-content: center; gap: 4px;
  animation: tile-slide 0.3s ease both;
  animation-delay: 2.95s;
}

/* beam connecting two phones */
.beam-line {
  position: absolute;
  top: 165px; left: 148px;
  width: 90px; height: 1px;
  background: linear-gradient(90deg, var(--gold), rgba(212,175,55,0));
  transform-origin: left center;
  animation: beam-appear 0.4s ease both;
  animation-delay: 1.5s;
  z-index: 5;
}
@keyframes beam-appear {
  from { scaleX: 0; opacity: 0; }
  to   { scaleX: 1; opacity: 1; }
}
.beam-particle {
  position: absolute;
  top: 160px; left: 148px;
  width: 6px; height: 6px; border-radius: 50%;
  background: var(--gold);
  animation: particle-travel 2s ease-in-out infinite;
  animation-delay: 1.6s;
  z-index: 6;
}
@keyframes particle-travel {
  0%   { left: 148px; opacity: 0; transform: scale(0.5); }
  10%  { opacity: 1; transform: scale(1); }
  90%  { opacity: 1; }
  100% { left: 228px; opacity: 0; transform: scale(0.5); }
}

/* ambient glow orbs */
.hero-orb-1 {
  position: absolute;
  width: 500px; height: 500px; border-radius: 50%;
  background: radial-gradient(circle, rgba(212,175,55,0.06) 0%, transparent 65%);
  top: -100px; right: -100px; pointer-events: none; z-index: 0;
}
.hero-orb-2 {
  position: absolute;
  width: 300px; height: 300px; border-radius: 50%;
  background: radial-gradient(circle, rgba(212,175,55,0.04) 0%, transparent 65%);
  bottom: 0; left: -80px; pointer-events: none; z-index: 0;
}

/* ── SECTION SHARED ── */
.lp-section {
  padding: 7rem 3rem;
  position: relative;
}
.lp-section-inner { max-width: 1180px; margin: 0 auto; }
.lp-eyebrow {
  display: block; font-size: 10px; font-weight: 500;
  letter-spacing: 3px; text-transform: uppercase;
  color: var(--gold); margin-bottom: 1.25rem; text-align: center;
}
.lp-section-title {
  font-family: var(--serif);
  font-size: clamp(2rem, 4vw, 3.2rem);
  font-weight: 300; line-height: 1.15; letter-spacing: -0.5px;
  color: var(--white); text-align: center; margin-bottom: 1.25rem;
}
.lp-section-title em { font-style: italic; color: var(--gold); }
.lp-section-sub {
  font-size: 15px; font-weight: 300; color: var(--muted);
  text-align: center; max-width: 480px; margin: 0 auto 4rem; line-height: 1.8;
}

/* ── GOLD DIVIDER ── */
.gold-rule {
  height: 0.5px;
  background: linear-gradient(90deg, transparent, rgba(212,175,55,0.3), transparent);
  margin: 0;
}

/* ── TRUST BAR ── */
.lp-trust {
  padding: 2rem 3rem;
  display: flex; align-items: center; justify-content: center;
  gap: 2.5rem; flex-wrap: wrap;
}
.lp-trust-label {
  font-size: 10px; font-weight: 400; color: var(--muted2);
  letter-spacing: 2px; text-transform: uppercase;
}
.lp-trust-items {
  display: flex; align-items: center; gap: 2rem; flex-wrap: wrap;
}
.lp-trust-item {
  display: flex; align-items: center; gap: 7px;
  font-size: 13px; font-weight: 400; color: var(--muted);
}
.lp-trust-sep { color: rgba(212,175,55,0.2); font-size: 18px; }

/* ── STEPS ── */
.lp-steps {
  display: grid; grid-template-columns: repeat(3, 1fr);
  gap: 1.5px;
  border: 0.5px solid var(--border);
  border-radius: 20px; overflow: hidden;
  margin-top: 1rem;
}
.lp-step {
  background: var(--card);
  padding: 2.5rem 2rem;
  position: relative; overflow: hidden;
  transition: background 0.3s;
  cursor: default;
}
.lp-step:hover { background: #1a1a1a; }
.lp-step::before {
  content: '';
  position: absolute; top: 0; left: 0; right: 0; height: 1px;
  background: linear-gradient(90deg, transparent, rgba(212,175,55,0), transparent);
  transition: background 0.3s;
}
.lp-step:hover::before {
  background: linear-gradient(90deg, transparent, rgba(212,175,55,0.4), transparent);
}
.lp-step-num {
  font-family: var(--serif); font-size: 3.5rem; font-weight: 300;
  color: rgba(212,175,55,0.12); line-height: 1;
  position: absolute; top: 1.5rem; right: 1.5rem;
  letter-spacing: -1px;
}
.lp-step-icon-wrap {
  width: 48px; height: 48px; border-radius: 14px;
  background: rgba(212,175,55,0.08);
  border: 0.5px solid rgba(212,175,55,0.2);
  display: flex; align-items: center; justify-content: center;
  font-size: 1.4rem; margin-bottom: 1.5rem;
}
.lp-step-title {
  font-family: var(--serif); font-size: 1.4rem; font-weight: 400;
  color: var(--white); margin-bottom: 0.75rem; line-height: 1.2;
}
.lp-step-desc {
  font-size: 14px; font-weight: 300; color: var(--muted);
  line-height: 1.75;
}

/* ── FEATURES ── */
.lp-feat-grid {
  display: grid; grid-template-columns: repeat(4, 1fr);
  gap: 1px;
  border: 0.5px solid var(--border);
  border-radius: 20px; overflow: hidden;
}
.lp-feat-card {
  background: var(--card); padding: 2rem 1.5rem;
  transition: background 0.3s;
  position: relative;
}
.lp-feat-card:hover { background: #181818; }
.lp-feat-icon {
  font-size: 1.6rem; margin-bottom: 1.25rem;
  display: block;
}
.lp-feat-title {
  font-family: var(--serif); font-size: 1.1rem; font-weight: 400;
  color: var(--white); margin-bottom: 0.5rem;
}
.lp-feat-desc {
  font-size: 13px; font-weight: 300; color: var(--muted); line-height: 1.7;
}

/* ── USE CASES ── */
.lp-use-tabs {
  display: flex; flex-wrap: wrap; gap: 0.5rem;
  justify-content: center; margin-bottom: 3rem;
}
.lp-use-tab {
  padding: 8px 18px; border-radius: 100px;
  font-size: 13px; font-weight: 400; cursor: pointer;
  transition: all 0.2s; font-family: var(--sans);
  display: flex; align-items: center; gap: 6px;
}
.lp-use-panel {
  display: grid; grid-template-columns: 1fr 1fr;
  gap: 3rem; align-items: center;
  background: var(--card);
  border: 0.5px solid var(--border);
  border-radius: 24px; padding: 3rem;
  position: relative; overflow: hidden;
}
.lp-use-panel::before {
  content: '';
  position: absolute; top: 0; left: 0; right: 0; height: 1px;
}
.lp-use-badge {
  display: inline-flex; align-items: center; gap: 8px;
  padding: 6px 14px; border-radius: 100px;
  margin-bottom: 1.5rem; font-size: 12px; font-weight: 500;
}
.lp-use-tagline {
  font-family: var(--serif); font-size: 2rem; font-weight: 300;
  color: var(--white); line-height: 1.2; margin-bottom: 1rem;
}
.lp-use-desc {
  font-size: 14px; font-weight: 300; color: var(--muted);
  line-height: 1.85; margin-bottom: 2rem;
}
.lp-use-highlights { display: flex; flex-direction: column; gap: 0.75rem; }
.lp-use-hl {
  display: flex; align-items: center; gap: 12px;
  background: #1a1a1a; border: 0.5px solid var(--border);
  border-radius: 12px; padding: 1rem 1.25rem;
}
.lp-use-hl-check {
  width: 28px; height: 28px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0; font-size: 11px; font-weight: 600;
}
.lp-use-hl-text {
  font-size: 13px; font-weight: 400; color: rgba(255,255,255,0.75);
}

/* ── PRICING ── */
.lp-pricing-wrap { display: flex; justify-content: center; margin-top: 1rem; }
.lp-pricing-card {
  background: var(--card);
  border: 0.5px solid rgba(212,175,55,0.25);
  border-radius: 28px; padding: 3rem 2.5rem;
  width: 100%; max-width: 420px;
  text-align: center; position: relative;
}
.lp-pricing-card::before {
  content: '';
  position: absolute; top: 0; left: 10%; right: 10%; height: 1px;
  background: linear-gradient(90deg, transparent, var(--gold), transparent);
}
.lp-pricing-badge {
  display: inline-block;
  background: linear-gradient(135deg, var(--gold), var(--gold2));
  color: #0A0A0A; border-radius: 100px;
  padding: 5px 16px; font-size: 11px; font-weight: 500;
  letter-spacing: 1px; text-transform: uppercase;
  margin-bottom: 2rem;
}
.lp-price-num {
  font-family: var(--serif); font-size: 5rem; font-weight: 300;
  color: var(--white); line-height: 1; letter-spacing: -2px;
}
.lp-price-period {
  font-size: 12px; font-weight: 400; color: var(--muted2);
  letter-spacing: 2px; text-transform: uppercase; margin: 0.5rem 0 2.5rem;
}
.lp-price-list { list-style: none; text-align: left; margin-bottom: 2.5rem; }
.lp-price-item {
  display: flex; align-items: center; gap: 10px;
  padding: 0.7rem 0; font-size: 14px; font-weight: 300;
  color: rgba(255,255,255,0.7);
  border-bottom: 0.5px solid var(--border);
}
.lp-price-check { color: var(--gold); font-size: 12px; flex-shrink: 0; }

/* ── CTA BANNER ── */
.lp-cta-banner {
  padding: 8rem 3rem;
  text-align: center;
  position: relative; overflow: hidden;
}
.lp-cta-banner::before {
  content: '';
  position: absolute; inset: 0;
  background: radial-gradient(ellipse 80% 60% at 50% 50%, rgba(212,175,55,0.07) 0%, transparent 70%);
  pointer-events: none;
}
.lp-cta-title {
  font-family: var(--serif);
  font-size: clamp(2.5rem, 6vw, 5rem);
  font-weight: 300; line-height: 1.1;
  color: var(--white); margin-bottom: 1.5rem;
  letter-spacing: -1px;
}
.lp-cta-title em { font-style: italic; color: var(--gold); }
.lp-cta-sub {
  font-size: 16px; font-weight: 300; color: var(--muted);
  margin-bottom: 3rem; line-height: 1.7;
}

/* ── FOOTER ── */
.lp-footer {
  padding: 3rem 3rem 4rem;
  border-top: 0.5px solid var(--border);
}
.lp-footer-inner {
  max-width: 1180px; margin: 0 auto;
  display: flex; flex-direction: column; align-items: center; gap: 1rem;
}
.lp-footer-logo {
  display: flex; align-items: center; gap: 10px;
  font-family: var(--sans); font-size: 15px; font-weight: 500; color: var(--white);
}
.lp-footer-logo em { color: var(--gold); font-style: normal; font-weight: 600; }
.lp-footer-tagline {
  font-size: 13px; font-weight: 300; color: var(--muted2);
}
.lp-footer-copy {
  font-size: 12px; font-weight: 300; color: rgba(255,255,255,0.18);
}

/* ── FADE-IN ON SCROLL ── */
.reveal {
  opacity: 0; transform: translateY(24px);
  transition: opacity 0.7s ease, transform 0.7s ease;
}
.reveal.visible { opacity: 1; transform: translateY(0); }

/* ── MOBILE ── */
@media (max-width: 900px) {
  .lp-hero { grid-template-columns: 1fr; text-align: center; padding: 8rem 1.5rem 4rem; }
  .lp-hero-sub { margin: 0 auto 3rem; }
  .lp-hero-ctas { justify-content: center; }
  .lp-hero-stats { justify-content: center; }
  .lp-hero-right { margin-top: 3rem; }
  .scan-scene { width: 300px; height: 380px; transform: scale(0.85); }
  .lp-steps { grid-template-columns: 1fr; }
  .lp-feat-grid { grid-template-columns: 1fr 1fr; }
  .lp-use-panel { grid-template-columns: 1fr; }
  .lp-nav { padding: 1rem 1.5rem; }
  .lp-nav.scrolled { padding: 0.75rem 1.5rem; }
  .lp-nav-links { gap: 1rem; }
  .lp-section { padding: 5rem 1.5rem; }
}
@media (max-width: 600px) {
  .lp-feat-grid { grid-template-columns: 1fr; }
  .lp-nav-link { display: none; }
}
`;

/* ─────────────────────────────────────────
   QR SCAN ANIMATION
───────────────────────────────────────── */
const QR_PATTERN = [
    [1, 1, 1, 0, 1, 0], [1, 0, 1, 1, 0, 1], [1, 1, 1, 0, 1, 1], [0, 1, 0, 1, 0, 0], [1, 0, 1, 1, 1, 0], [0, 1, 0, 0, 1, 1]
];

function ScanScene() {
    return (
        <div className="scan-scene">
            <div className="hero-orb-1" />

            {/* Phone A — scanner */}
            <div className="phone-scanner">
                <div className="phone-scanner-screen">
                    <div className="scanner-viewfinder">
                        <div className="vf-corner-tr" />
                        <div className="vf-corner-bl" />
                        <div className="scan-laser" />
                        {/* mini qr */}
                        <div className="mini-qr">
                            {QR_PATTERN.map((row, ri) =>
                                row.map((cell, ci) => (
                                    <div key={`${ri}-${ci}`} className="mini-qr-cell"
                                        style={{ background: cell ? "rgba(212,175,55,0.5)" : "transparent" }} />
                                ))
                            )}
                        </div>
                    </div>
                    <div className="scanner-label">Point at QR code</div>
                </div>
            </div>

            {/* Beam + particle */}
            <div className="beam-line" />
            <div className="beam-particle" />

            {/* Phone B — profile card opening */}
            <div className="phone-profile">
                <div className="profile-card-wrap">
                    {/* Banner */}
                    <div className="profile-banner">
                        <div className="profile-banner-img" />
                        <div className="profile-banner-fade" />
                        {/* floating avatar */}
                        <div className="profile-avatar-ring">
                            <div className="profile-avatar-inner">MG</div>
                        </div>
                    </div>

                    {/* Info */}
                    <div className="profile-info">
                        <div className="profile-info-name">
                            Mragank <em>Gaur</em>
                        </div>
                        <div className="profile-info-role">Director · Empower Pragati</div>

                        <div className="profile-tiles">
                            {[["📞", "Phone"], ["✉️", "Email"], ["📍", "Delhi"]].map(([icon, label]) => (
                                <div className="profile-tile" key={label}>
                                    <div className="tile-dot"><div className="tile-dot-inner" /></div>
                                    <div className="tile-text">{label}</div>
                                </div>
                            ))}
                        </div>

                        <button className="profile-save-btn">
                            ↓ Save Contact
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

/* ─────────────────────────────────────────
   USE CASES
───────────────────────────────────────── */
const USE_CASES = [
    {
        icon: "👔", role: "Job Seekers", gold: "rgba(212,175,55,1)",
        tagline: "Land your next opportunity faster",
        desc: "Walk into every interview with a digital card that includes your resume, LinkedIn, GitHub, and portfolio — all in one elegant scan.",
        highlights: ["Resume attached & downloadable", "LinkedIn & GitHub links", "QR code for instant sharing", "Save contact in one tap"]
    },
    {
        icon: "💼", role: "Freelancers", gold: "rgba(212,175,55,1)",
        tagline: "Win clients before you even speak",
        desc: "Share everything a client needs — portfolio, social proof, and contact — all in a single professional link. Close deals faster.",
        highlights: ["Portfolio & website link", "WhatsApp quick contact", "Instagram & social proof", "Professional digital presence"]
    },
    {
        icon: "🏢", role: "Business Owners", gold: "rgba(212,175,55,1)",
        tagline: "Network smarter, not harder",
        desc: "Stop spending on printed cards that get lost. Share via QR at events — always updated, never outdated, never thrown away.",
        highlights: ["Always up-to-date details", "Google Wallet for offline use", "QR code for events & meetups", "Share via WhatsApp or email"]
    },
    {
        icon: "🎓", role: "Students", gold: "rgba(212,175,55,1)",
        tagline: "Build your professional identity early",
        desc: "Stand out in campus placements and internship drives. Show your skills, projects, and social profiles before you say a word.",
        highlights: ["GitHub & project links", "Resume upload for placements", "LinkedIn profile showcase", "Free forever — no cost"]
    },
    {
        icon: "📣", role: "Sales Pros", gold: "rgba(212,175,55,1)",
        tagline: "Close the follow-up gap instantly",
        desc: "At trade shows or cold visits — one QR scan and your prospect has everything. Number, email, website, product links in one place.",
        highlights: ["Instant QR share at events", "Website & product links", "Save contact in one tap", "Google Wallet for quick access"]
    },
    {
        icon: "🧑‍💻", role: "Developers", gold: "rgba(212,175,55,1)",
        tagline: "Your entire dev profile in one link",
        desc: "GitHub, LinkedIn, Twitter, personal site, resume — all in one clean card. Share at hackathons or in open source communities.",
        highlights: ["GitHub & portfolio links", "Twitter / X profile", "Resume for job applications", "Professional dev identity"]
    },
];

function UseCasesSection({ navigate }) {
    const [active, setActive] = useState(0);
    const cur = USE_CASES[active];
    return (
        <section className="lp-section">
            <div className="lp-section-inner">
                <span className="lp-eyebrow reveal">Who is it for</span>
                <h2 className="lp-section-title reveal">Built for every <em>professional</em></h2>
                <p className="lp-section-sub reveal">Whether you're a student or a CEO — your digital card works for you.</p>

                <div className="lp-use-tabs reveal">
                    {USE_CASES.map((u, i) => (
                        <button key={i} className="lp-use-tab" onClick={() => setActive(i)}
                            style={{
                                border: active === i ? `0.5px solid rgba(212,175,55,0.5)` : "0.5px solid rgba(255,255,255,0.1)",
                                background: active === i ? "rgba(212,175,55,0.1)" : "transparent",
                                color: active === i ? "var(--gold)" : "var(--muted)",
                            }}>
                            {u.icon} {u.role}
                        </button>
                    ))}
                </div>

                <div className="lp-use-panel"
                    style={{ borderColor: "rgba(212,175,55,0.15)" }}>
                    <div className="lp-use-panel::before" style={{}} />
                    <div>
                        <div className="lp-use-badge"
                            style={{ background: "rgba(212,175,55,0.1)", color: "var(--gold)", border: "0.5px solid rgba(212,175,55,0.25)" }}>
                            <span>{cur.icon}</span><span style={{ fontSize: "12px", letterSpacing: "0.5px" }}>{cur.role}</span>
                        </div>
                        <div className="lp-use-tagline">{cur.tagline}</div>
                        <p className="lp-use-desc">{cur.desc}</p>
                        <button className="lp-btn-gold" onClick={() => navigate("/create")}>
                            Create My Card — Free →
                        </button>
                    </div>
                    <div className="lp-use-highlights">
                        {cur.highlights.map((h, i) => (
                            <div className="lp-use-hl" key={i}>
                                <div className="lp-use-hl-check"
                                    style={{ background: "rgba(212,175,55,0.1)", color: "var(--gold)" }}>✓</div>
                                <span className="lp-use-hl-text">{h}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}

/* ─────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────── */
export default function LandingPage() {
    const navigate = useNavigate();
    const [scrolled, setScrolled] = useState(false);

    /* scroll nav */
    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 40);
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    /* scroll reveal */
    useEffect(() => {
        const els = document.querySelectorAll(".reveal");
        const observer = new IntersectionObserver(
            (entries) => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add("visible"); }),
            { threshold: 0.12 }
        );
        els.forEach(el => observer.observe(el));
        return () => observer.disconnect();
    }, []);

    return (
        <>
            <style dangerouslySetInnerHTML={{ __html: GLOBAL_CSS }} />

            {/* ── NAV ── */}
            <nav className={`lp-nav${scrolled ? " scrolled" : ""}`}>
                <div className="lp-nav-inner">
                    <a href="/" className="lp-logo">
                        {/* inline logo icon */}
                        <svg className="lp-logo-icon" viewBox="0 0 34 34" fill="none">
                            <rect width="34" height="34" rx="9" fill="#1A1A1A" />
                            <rect width="34" height="34" rx="9" stroke="rgba(212,175,55,0.2)" strokeWidth="0.5" />
                            <circle cx="15" cy="13" r="5" stroke="#D4AF37" strokeWidth="1.5" fill="none" />
                            <path d="M7 27 C7 21 10 18 15 18 C20 18 23 21 23 27" stroke="#D4AF37" strokeWidth="1.5" strokeLinecap="round" fill="none" />
                            <path d="M23 11 L27 11 L27 23" stroke="rgba(212,175,55,0.5)" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                            <circle cx="27" cy="23" r="1.8" fill="#D4AF37" />
                        </svg>
                        myprofile<em>corner</em>
                    </a>
                    <div className="lp-nav-links">
                        <a href="#how" className="lp-nav-link">How it works</a>
                        <a href="#features" className="lp-nav-link">Features</a>
                        <a href="#pricing" className="lp-nav-link">Pricing</a>
                        <button className="lp-nav-cta" onClick={() => navigate("/create")}>
                            Create Free Card
                        </button>
                    </div>
                </div>
            </nav>

            {/* ── HERO ── */}
            <section style={{ background: "var(--black)", overflow: "hidden", position: "relative" }}>
                <div className="lp-hero">
                    {/* ambient bg orbs */}
                    <div style={{
                        position: "absolute", width: "600px", height: "600px", borderRadius: "50%",
                        background: "radial-gradient(circle, rgba(212,175,55,0.05) 0%, transparent 65%)",
                        top: "-200px", left: "-100px", pointerEvents: "none"
                    }} />

                    {/* LEFT */}
                    <div className="lp-hero-left">
                        <div className="lp-hero-eyebrow">
                            <div className="lp-hero-eyebrow-dot" />
                            100% Free · No App Needed
                        </div>

                        <h1 className="lp-hero-title">
                            Your Digital<br />
                            Identity, <em>Always</em><br />
                            in Your Pocket
                        </h1>

                        <p className="lp-hero-sub">
                            Replace paper visiting cards with a smart digital card. Share your profile
                            instantly via QR code, link, or Google Wallet — no printing, no waste.
                        </p>

                        <div className="lp-hero-ctas">
                            <button className="lp-btn-gold" onClick={() => navigate("/create")}>
                                Create My Card — It's Free →
                            </button>
                            <a href="#how" className="lp-btn-ghost">See how it works</a>
                        </div>

                        <div className="lp-hero-stats">
                            {[["100%", "Free forever"], ["2 min", "Setup time"], ["0", "Paper waste"]].map(([n, l]) => (
                                <div key={l}>
                                    <div className="lp-stat-num">{n}</div>
                                    <div className="lp-stat-label">{l}</div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* RIGHT — animated scan scene */}
                    <div className="lp-hero-right">
                        <ScanScene />
                    </div>
                </div>
            </section>

            <div className="gold-rule" />

            {/* ── TRUST BAR ── */}
            <div className="lp-trust">
                <span className="lp-trust-label">Works seamlessly with</span>
                <div className="lp-trust-items">
                    {[["🔥", "Firebase"], ["🪙", "Google Wallet"], ["📱", "Android & iOS"], ["🔗", "Any Browser"]].map(([icon, label], i, arr) => (
                        <React.Fragment key={label}>
                            <div className="lp-trust-item"><span>{icon}</span><span>{label}</span></div>
                            {i < arr.length - 1 && <span className="lp-trust-sep">·</span>}
                        </React.Fragment>
                    ))}
                </div>
            </div>

            <div className="gold-rule" />

            {/* ── HOW IT WORKS ── */}
            <section id="how" className="lp-section">
                <div className="lp-section-inner">
                    <span className="lp-eyebrow reveal">How it works</span>
                    <h2 className="lp-section-title reveal">From setup to sharing <em>in 3 steps</em></h2>
                    <p className="lp-section-sub reveal">No app downloads. No tech skills required. Just fill, share, impress.</p>

                    <div className="lp-steps reveal">
                        {[
                            { num: "01", icon: "✏️", title: "Fill your profile", desc: "Add your name, photo, contact details, social links, resume, and website. Takes less than 2 minutes." },
                            { num: "02", icon: "🎴", title: "Get your digital card", desc: "Instantly receive a beautiful card with a unique QR code and shareable link — no design skills needed." },
                            { num: "03", icon: "🚀", title: "Share anywhere", desc: "Show your QR in meetings, add to Google Wallet, or send your link on WhatsApp, email, or LinkedIn." },
                        ].map((step) => (
                            <div className="lp-step" key={step.num}>
                                <div className="lp-step-num">{step.num}</div>
                                <div className="lp-step-icon-wrap">{step.icon}</div>
                                <div className="lp-step-title">{step.title}</div>
                                <p className="lp-step-desc">{step.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <div className="gold-rule" />

            {/* ── FEATURES ── */}
            <section id="features" className="lp-section">
                <div className="lp-section-inner">
                    <span className="lp-eyebrow reveal">Features</span>
                    <h2 className="lp-section-title reveal">Everything a modern <em>professional</em> needs</h2>

                    <div className="lp-feat-grid reveal">
                        {[
                            { icon: "📸", title: "Profile Photo", desc: "Upload your photo — it appears on your card and even in your Google Wallet pass." },
                            { icon: "🔗", title: "All Social Links", desc: "LinkedIn, GitHub, Instagram, Twitter, WhatsApp, YouTube — show what matters." },
                            { icon: "📄", title: "Resume Attachment", desc: "Attach your PDF resume directly to your card. Recruiters can download it instantly." },
                            { icon: "🪙", title: "Google Wallet", desc: "Add your card to Google Wallet and show it offline anytime, anywhere." },
                            { icon: "📲", title: "QR Code", desc: "Every card gets a unique QR code. One scan opens your full profile instantly." },
                            { icon: "💾", title: "Save Contact", desc: "Visitors can save your details directly to their phone contacts with one tap." },
                            { icon: "✏️", title: "Edit Anytime", desc: "Changed jobs? New number? Update your card instantly — the link stays the same." },
                            { icon: "🌐", title: "Works Everywhere", desc: "Your card opens in any browser on any device. No app install required." },
                        ].map((f) => (
                            <div className="lp-feat-card" key={f.title}>
                                <span className="lp-feat-icon">{f.icon}</span>
                                <div className="lp-feat-title">{f.title}</div>
                                <p className="lp-feat-desc">{f.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <div className="gold-rule" />

            {/* ── USE CASES ── */}
            <UseCasesSection navigate={navigate} />

            <div className="gold-rule" />

            {/* ── PRICING ── */}
            <section id="pricing" className="lp-section">
                <div className="lp-section-inner">
                    <span className="lp-eyebrow reveal">Pricing</span>
                    <h2 className="lp-section-title reveal">Simple, <em>honest</em> pricing</h2>
                    <p className="lp-section-sub reveal">No hidden charges. No credit card needed.</p>

                    <div className="lp-pricing-wrap reveal">
                        <div className="lp-pricing-card">
                            <div className="lp-pricing-badge">Most Popular</div>
                            <div className="lp-price-num">₹0</div>
                            <div className="lp-price-period">Forever Free</div>
                            <ul className="lp-price-list">
                                {["1 Digital Visiting Card", "Unique QR Code & Link", "Profile Photo Upload", "Resume Upload", "All Social Media Links", "Google Wallet Pass", "Save Contact (.vcf)", "Edit Anytime", "No App Required"].map(item => (
                                    <li className="lp-price-item" key={item}>
                                        <span className="lp-price-check">✦</span>{item}
                                    </li>
                                ))}
                            </ul>
                            <button className="lp-btn-gold" style={{ width: "100%", justifyContent: "center" }} onClick={() => navigate("/create")}>
                                Get Started Free →
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            <div className="gold-rule" />

            {/* ── CTA BANNER ── */}
            <section className="lp-cta-banner">
                <h2 className="lp-cta-title reveal">
                    Stop carrying<br /><em>paper cards.</em>
                </h2>
                <p className="lp-cta-sub reveal">Create your digital card in under 2 minutes. Free, forever.</p>
                <button className="lp-btn-gold reveal" style={{ margin: "0 auto" }} onClick={() => navigate("/create")}>
                    Create My Card Now →
                </button>
            </section>

            <div className="gold-rule" />

            {/* ── FOOTER ── */}
            <footer className="lp-footer">
                <div className="lp-footer-inner">
                    <div className="lp-footer-logo">
                        <svg width="28" height="28" viewBox="0 0 34 34" fill="none">
                            <rect width="34" height="34" rx="9" fill="#1A1A1A" />
                            <rect width="34" height="34" rx="9" stroke="rgba(212,175,55,0.2)" strokeWidth="0.5" />
                            <circle cx="15" cy="13" r="5" stroke="#D4AF37" strokeWidth="1.5" fill="none" />
                            <path d="M7 27 C7 21 10 18 15 18 C20 18 23 21 23 27" stroke="#D4AF37" strokeWidth="1.5" strokeLinecap="round" fill="none" />
                            <path d="M23 11 L27 11 L27 23" stroke="rgba(212,175,55,0.5)" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                            <circle cx="27" cy="23" r="1.8" fill="#D4AF37" />
                        </svg>
                        myprofile<em>corner</em>
                    </div>
                    <p className="lp-footer-tagline">Your digital identity, always in your pocket.</p>
                    <p className="lp-footer-copy">© {new Date().getFullYear()} MyProfileCorner · Made with ♥ in India</p>
                </div>
            </footer>
        </>
    );
}


// import React, { useState, useEffect } from "react";
// import { useNavigate } from "react-router-dom";

// export default function LandingPage() {
//     const navigate = useNavigate();
//     const [scrolled, setScrolled] = useState(false);
//     const [activeStep, setActiveStep] = useState(0);

//     useEffect(() => {
//         const onScroll = () => setScrolled(window.scrollY > 40);
//         window.addEventListener("scroll", onScroll);
//         return () => window.removeEventListener("scroll", onScroll);
//     }, []);

//     useEffect(() => {
//         const interval = setInterval(() => {
//             setActiveStep((prev) => (prev + 1) % 3);
//         }, 2500);
//         return () => clearInterval(interval);
//     }, []);

//     return (
//         <div style={s.root}>

//             {/* ── NAV ── */}
//             <nav style={{ ...s.nav, ...(scrolled ? s.navScrolled : {}) }}>
//                 <div style={s.navInner}>
//                     <span style={{ ...s.logo, color: scrolled ? "#1a1a2e" : "#fff" }}>
//                         <span style={s.logoDot}>●</span> MyProfileCorner
//                     </span>
//                     <div style={s.navLinks}>
//                         <a href="#how" style={{ ...s.navLink, color: scrolled ? "#6b7280" : "rgba(255,255,255,0.8)" }}>How it works</a>
//                         <a href="#features" style={{ ...s.navLink, color: scrolled ? "#6b7280" : "rgba(255,255,255,0.8)" }}>Features</a>
//                         <a href="#pricing" style={{ ...s.navLink, color: scrolled ? "#6b7280" : "rgba(255,255,255,0.8)" }}>Pricing</a>
//                         <button onClick={() => navigate("/create")} style={{
//                             ...s.navCta,
//                             ...(scrolled ? {} : { background: "#fff", color: "#1a1a2e" })
//                         }}>
//                             Create Free Card
//                         </button>
//                     </div>
//                 </div>
//             </nav>

//             {/* ── HERO ── */}
//             <section style={s.hero}>
//                 <div style={s.heroBadge}>✦ 100% Free · No App Needed</div>
//                 <h1 style={s.heroTitle}>
//                     Your Digital Identity,<br />
//                     <span style={s.heroAccent}>Always in Your Pocket</span>
//                 </h1>
//                 <p style={s.heroSub}>
//                     Replace paper visiting cards with a smart digital card. Share your profile
//                     instantly via QR code, link, or Google Wallet — no printing, no waste.
//                 </p>
//                 <div style={s.heroCtas}>
//                     <button onClick={() => navigate("/create")} style={s.btnPrimary}>
//                         Create My Card — It's Free →
//                     </button>
//                     <a href="#how" style={s.btnGhost}>See how it works</a>
//                 </div>

//                 {/* Floating card mockup */}
//                 <div style={s.mockupWrap}>
//                     <div style={s.mockCard}>
//                         <div style={s.mockHeader}>
//                             <div style={s.mockAvatar}>RS</div>
//                             <div>
//                                 <div style={s.mockName}>Rahul Singh</div>
//                                 <div style={s.mockRole}>Product Designer · Empower Pragati</div>
//                             </div>
//                         </div>
//                         <div style={s.mockDivider} />
//                         <div style={s.mockRow}><span>📞</span><span>+91 98765 43210</span></div>
//                         <div style={s.mockRow}><span>✉️</span><span>rahul@example.com</span></div>
//                         <div style={s.mockRow}><span>📍</span><span>Greater Noida, UP</span></div>
//                         <div style={s.mockDivider} />
//                         <div style={s.mockQrArea}>
//                             <div style={s.mockQr}>
//                                 {[...Array(5)].map((_, i) => (
//                                     <div key={i} style={s.mockQrRow}>
//                                         {[...Array(5)].map((_, j) => (
//                                             <div key={j} style={{
//                                                 ...s.mockQrCell,
//                                                 background: Math.random() > 0.5 ? "#1a1a2e" : "transparent"
//                                             }} />
//                                         ))}
//                                     </div>
//                                 ))}
//                             </div>
//                             <span style={s.mockQrLabel}>Scan to view card</span>
//                         </div>
//                         <div style={s.mockWalletBtn}>🪙 Add to Google Wallet</div>
//                     </div>
//                     <div style={s.mockGlow} />
//                 </div>
//             </section>

//             {/* ── LOGOS / TRUST BAR ── */}
//             <div style={s.trustBar}>
//                 <span style={s.trustLabel}>Works seamlessly with</span>
//                 <div style={s.trustIcons}>
//                     <span style={s.trustIcon}>🔥 Firebase</span>
//                     <span style={s.trustDot}>·</span>
//                     <span style={s.trustIcon}>🪙 Google Wallet</span>
//                     <span style={s.trustDot}>·</span>
//                     <span style={s.trustIcon}>📱 Android & iOS</span>
//                     <span style={s.trustDot}>·</span>
//                     <span style={s.trustIcon}>🔗 Any Browser</span>
//                 </div>
//             </div>

//             {/* ── HOW IT WORKS ── */}
//             <section id="how" style={s.section}>
//                 <div style={s.sectionInner}>
//                     <p style={s.sectionEyebrow}>How it works</p>
//                     <h2 style={s.sectionTitle}>From setup to sharing in 3 steps</h2>
//                     <p style={s.sectionSub}>No app downloads. No tech skills required. Just fill, share, impress.</p>

//                     <div style={s.steps}>
//                         {[
//                             {
//                                 num: "01",
//                                 icon: "✏️",
//                                 title: "Fill your profile",
//                                 desc: "Add your name, photo, contact details, social links, resume, and website. Takes less than 2 minutes."
//                             },
//                             {
//                                 num: "02",
//                                 icon: "🎴",
//                                 title: "Get your digital card",
//                                 desc: "Instantly get a beautiful card with a unique QR code and shareable link — no design skills needed."
//                             },
//                             {
//                                 num: "03",
//                                 icon: "🚀",
//                                 title: "Share anywhere",
//                                 desc: "Show your QR in meetings, add to Google Wallet, or send your link on WhatsApp, email, or LinkedIn."
//                             },
//                         ].map((step, i) => (
//                             <div
//                                 key={i}
//                                 style={{
//                                     ...s.stepCard,
//                                     ...(activeStep === i ? s.stepCardActive : {})
//                                 }}
//                                 onMouseEnter={() => setActiveStep(i)}
//                             >
//                                 <div style={s.stepNum}>{step.num}</div>
//                                 <div style={s.stepIcon}>{step.icon}</div>
//                                 <h3 style={s.stepTitle}>{step.title}</h3>
//                                 <p style={s.stepDesc}>{step.desc}</p>
//                             </div>
//                         ))}
//                     </div>
//                 </div>
//             </section>

//             {/* ── FEATURES ── */}
//             <section id="features" style={{ ...s.section, background: "#f8f9ff" }}>
//                 <div style={s.sectionInner}>
//                     <p style={s.sectionEyebrow}>Features</p>
//                     <h2 style={s.sectionTitle}>Everything a modern professional needs</h2>

//                     <div style={s.featGrid}>
//                         {[
//                             { icon: "📸", title: "Profile Photo", desc: "Upload your photo — it appears on your card and even in your Google Wallet pass." },
//                             { icon: "🔗", title: "All Social Links", desc: "LinkedIn, GitHub, Instagram, Twitter, WhatsApp, YouTube — show what matters to you." },
//                             { icon: "📄", title: "Resume Attachment", desc: "Attach your PDF resume directly to your card. Recruiters can download it instantly." },
//                             { icon: "🪙", title: "Google Wallet", desc: "Add your card to Google Wallet and show it offline anytime, anywhere." },
//                             { icon: "📲", title: "QR Code", desc: "Every card gets a unique QR code. One scan opens your full profile instantly." },
//                             { icon: "💾", title: "Save Contact", desc: "Visitors can save your details directly to their phone contacts with one tap." },
//                             { icon: "✏️", title: "Edit Anytime", desc: "Changed jobs? New number? Update your card instantly — the link stays the same." },
//                             { icon: "🌐", title: "Works Everywhere", desc: "Your card opens in any browser on any device. No app install required." },
//                         ].map((f, i) => (
//                             <div key={i} style={s.featCard}>
//                                 <div style={s.featIcon}>{f.icon}</div>
//                                 <h3 style={s.featTitle}>{f.title}</h3>
//                                 <p style={s.featDesc}>{f.desc}</p>
//                             </div>
//                         ))}
//                     </div>
//                 </div>
//             </section>

//             {/* ── USE CASES ── */}
//             <UseCaseSection navigate={navigate} />

//             {/* ── PRICING ── */}
//             <section id="pricing" style={{ ...s.section, background: "#f8f9ff" }}>
//                 <div style={s.sectionInner}>
//                     <p style={s.sectionEyebrow}>Pricing</p>
//                     <h2 style={s.sectionTitle}>Simple, honest pricing</h2>
//                     <p style={s.sectionSub}>No hidden charges. No credit card needed.</p>

//                     <div style={s.pricingWrap}>
//                         <div style={s.pricingCard}>
//                             <div style={s.pricingBadge}>Most Popular</div>
//                             <div style={s.pricingAmount}>₹0</div>
//                             <div style={s.pricingPeriod}>Forever Free</div>
//                             <ul style={s.pricingList}>
//                                 {[
//                                     "1 Digital Visiting Card",
//                                     "Unique QR Code & Link",
//                                     "Profile Photo Upload",
//                                     "Resume Upload",
//                                     "All Social Media Links",
//                                     "Google Wallet Pass",
//                                     "Save Contact (.vcf)",
//                                     "Edit Anytime",
//                                     "No App Required",
//                                 ].map((item, i) => (
//                                     <li key={i} style={s.pricingItem}>
//                                         <span style={s.pricingCheck}>✓</span> {item}
//                                     </li>
//                                 ))}
//                             </ul>
//                             <button onClick={() => navigate("/create")} style={s.btnPrimary}>
//                                 Get Started Free →
//                             </button>
//                         </div>
//                     </div>
//                 </div>
//             </section>

//             {/* ── CTA BANNER ── */}
//             <section style={s.ctaBanner}>
//                 <h2 style={s.ctaTitle}>Stop carrying paper cards.</h2>
//                 <p style={s.ctaSub}>Create your digital card in under 2 minutes. Free, forever.</p>
//                 <button onClick={() => navigate("/create")} style={s.ctaBtn}>
//                     Create My Card Now →
//                 </button>
//             </section>

//             {/* ── FOOTER ── */}
//             <footer style={s.footer}>
//                 <div style={s.footerInner}>
//                     <span style={s.footerLogo}>
//                         <span style={s.logoDot}>●</span> MyProfileCorner
//                     </span>
//                     <p style={s.footerTagline}>Your digital identity, always in your pocket.</p>
//                     <p style={s.footerCopy}>© {new Date().getFullYear()} MyProfileCorner. Made with ❤️ in India.</p>
//                 </div>
//             </footer>

//         </div>
//     );
// }

// const USE_CASES = [
//     {
//         icon: "👔", role: "Job Seekers", color: "#4f46e5",
//         tagline: "Land your next job faster",
//         desc: "Walk into every interview with a digital card that includes your resume, LinkedIn, GitHub, and portfolio — all in one scan. No more handing out paper.",
//         highlights: ["Resume attached & downloadable", "LinkedIn & GitHub links", "QR code for instant sharing", "Save contact in one tap"],
//     },
//     {
//         icon: "💼", role: "Freelancers", color: "#0891b2",
//         tagline: "Win clients before you even speak",
//         desc: "Share everything a client needs — your portfolio, social proof, testimonials, and contact — all in a single professional link. Close deals faster.",
//         highlights: ["Portfolio & website link", "WhatsApp quick contact", "Instagram & social proof", "Professional digital presence"],
//     },
//     {
//         icon: "🏢", role: "Business Owners", color: "#059669",
//         tagline: "Network smarter, not harder",
//         desc: "Stop spending money on printed cards that get lost or thrown away. Share your business card at events via QR code — always updated, never outdated.",
//         highlights: ["Always up-to-date details", "Google Wallet for offline use", "QR code for events & meetups", "Share via WhatsApp or email"],
//     },
//     {
//         icon: "🎓", role: "Students", color: "#d97706",
//         tagline: "Build your professional identity early",
//         desc: "Stand out in campus placements, internship drives, and professor meetings. Show your skills, projects, and social profiles before you even say a word.",
//         highlights: ["GitHub & project links", "Resume upload for placements", "LinkedIn profile showcase", "Free forever — no cost"],
//     },
//     {
//         icon: "📣", role: "Sales Professionals", color: "#dc2626",
//         tagline: "Close the follow-up gap instantly",
//         desc: "At trade shows, exhibitions, or cold visits — one QR scan and your prospect has everything. Your number, email, website, and product links all in one place.",
//         highlights: ["Instant QR share at events", "Website & product links", "Save contact in one tap", "Google Wallet for quick access"],
//     },
//     {
//         icon: "🧑‍💻", role: "Developers", color: "#7c3aed",
//         tagline: "Your entire dev profile in one link",
//         desc: "GitHub, LinkedIn, Twitter, personal site, resume — all in one clean card. Share it in hackathons, open source communities, or job applications.",
//         highlights: ["GitHub & portfolio links", "Twitter / X profile", "Resume for job applications", "Professional dev identity"],
//     },
// ];

// function UseCaseSection({ navigate }) {
//     const [active, setActive] = useState(0);
//     const current = USE_CASES[active];

//     return (
//         <section style={{ padding: "5rem 1.5rem", background: "#fff" }}>
//             <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
//                 <p style={{ fontSize: "0.75rem", fontWeight: "700", color: "#6366f1", textTransform: "uppercase", letterSpacing: "2px", textAlign: "center", margin: "0 0 0.75rem" }}>
//                     Who is it for?
//                 </p>
//                 <h2 style={{ fontSize: "clamp(1.6rem, 4vw, 2.5rem)", fontWeight: "900", textAlign: "center", color: "#1a1a2e", margin: "0 0 0.75rem", lineHeight: 1.2 }}>
//                     Built for every professional
//                 </h2>
//                 <p style={{ fontSize: "1rem", color: "#6b7280", textAlign: "center", maxWidth: "500px", margin: "0 auto 3rem", lineHeight: 1.7 }}>
//                     Whether you're a student or a CEO — your digital card works for you.
//                 </p>

//                 {/* Tab pills */}
//                 <div style={{ display: "flex", flexWrap: "wrap", gap: "0.6rem", justifyContent: "center", marginBottom: "2.5rem" }}>
//                     {USE_CASES.map((u, i) => (
//                         <button
//                             key={i}
//                             onClick={() => setActive(i)}
//                             style={{
//                                 padding: "0.5rem 1.1rem",
//                                 borderRadius: "50px",
//                                 border: active === i ? `2px solid ${u.color}` : "2px solid #e5e7eb",
//                                 background: active === i ? u.color : "#fff",
//                                 color: active === i ? "#fff" : "#6b7280",
//                                 fontWeight: "700", fontSize: "0.85rem",
//                                 cursor: "pointer", transition: "all 0.2s",
//                                 display: "flex", alignItems: "center", gap: "0.4rem",
//                             }}
//                         >
//                             {u.icon} {u.role}
//                         </button>
//                     ))}
//                 </div>

//                 {/* Detail panel */}
//                 <div style={{
//                     display: "grid",
//                     gridTemplateColumns: "1fr 1fr",
//                     gap: "2rem", alignItems: "center",
//                     background: "#f8f9ff", borderRadius: "24px",
//                     padding: "2.5rem", border: `2px solid ${current.color}22`,
//                 }}>
//                     {/* Left */}
//                     <div>
//                         <div style={{
//                             display: "inline-flex", alignItems: "center", gap: "0.5rem",
//                             background: `${current.color}18`, borderRadius: "50px",
//                             padding: "0.4rem 1rem", marginBottom: "1.25rem",
//                         }}>
//                             <span style={{ fontSize: "1.2rem" }}>{current.icon}</span>
//                             <span style={{ fontSize: "0.8rem", fontWeight: "700", color: current.color }}>{current.role}</span>
//                         </div>
//                         <h3 style={{ fontSize: "1.6rem", fontWeight: "900", color: "#1a1a2e", margin: "0 0 1rem", lineHeight: 1.25 }}>
//                             {current.tagline}
//                         </h3>
//                         <p style={{ fontSize: "0.95rem", color: "#6b7280", lineHeight: 1.8, margin: "0 0 1.5rem" }}>
//                             {current.desc}
//                         </p>
//                         <button
//                             onClick={() => navigate("/create")}
//                             style={{
//                                 padding: "0.8rem 1.8rem",
//                                 background: current.color,
//                                 color: "#fff", border: "none", borderRadius: "10px",
//                                 fontSize: "0.95rem", fontWeight: "700", cursor: "pointer",
//                             }}
//                         >
//                             Create My Card Free →
//                         </button>
//                     </div>

//                     {/* Right — highlights */}
//                     <div style={{ display: "flex", flexDirection: "column", gap: "0.85rem" }}>
//                         {current.highlights.map((h, i) => (
//                             <div key={i} style={{
//                                 display: "flex", alignItems: "center", gap: "0.85rem",
//                                 background: "#fff", borderRadius: "12px",
//                                 padding: "1rem 1.25rem",
//                                 border: "1.5px solid #e5e7eb",
//                                 boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
//                             }}>
//                                 <div style={{
//                                     width: "32px", height: "32px", borderRadius: "50%",
//                                     background: `${current.color}18`,
//                                     display: "flex", alignItems: "center", justifyContent: "center",
//                                     flexShrink: 0,
//                                 }}>
//                                     <span style={{ color: current.color, fontWeight: "800", fontSize: "0.9rem" }}>✓</span>
//                                 </div>
//                                 <span style={{ fontSize: "0.9rem", fontWeight: "600", color: "#374151" }}>{h}</span>
//                             </div>
//                         ))}
//                     </div>
//                 </div>
//             </div>
//         </section>
//     );
// }

// const s = {
//     root: {
//         fontFamily: "'Segoe UI', Arial, sans-serif",
//         color: "#1a1a2e",
//         overflowX: "hidden",
//     },

//     // NAV
//     nav: {
//         position: "fixed", top: 0, left: 0, right: 0, zIndex: 100,
//         padding: "1.2rem 2rem",
//         transition: "all 0.3s",
//     },
//     navScrolled: {
//         background: "rgba(255,255,255,0.95)",
//         backdropFilter: "blur(12px)",
//         boxShadow: "0 2px 20px rgba(0,0,0,0.08)",
//         padding: "0.8rem 2rem",
//     },
//     navInner: {
//         maxWidth: "1100px", margin: "0 auto",
//         display: "flex", alignItems: "center", justifyContent: "space-between",
//     },
//     logo: {
//         fontSize: "1.2rem", fontWeight: "800", color: "#1a1a2e",
//         display: "flex", alignItems: "center", gap: "6px",
//     },
//     logoDot: { color: "#6366f1", fontSize: "0.8rem" },
//     navLinks: { display: "flex", alignItems: "center", gap: "2rem" },
//     navLink: {
//         color: "#6b7280", textDecoration: "none",
//         fontSize: "0.9rem", fontWeight: "500",
//     },
//     navCta: {
//         padding: "0.55rem 1.2rem",
//         background: "linear-gradient(135deg, #1a1a2e, #0f3460)",
//         color: "#fff", border: "none", borderRadius: "8px",
//         fontSize: "0.85rem", fontWeight: "700", cursor: "pointer",
//     },

//     // HERO
//     hero: {
//         minHeight: "100vh",
//         background: "linear-gradient(135deg, #0f0c29 0%, #1a1a2e 40%, #0f3460 100%)",
//         display: "flex", flexDirection: "column",
//         alignItems: "center", justifyContent: "center",
//         textAlign: "center", padding: "8rem 1.5rem 4rem",
//         position: "relative", overflow: "hidden",
//     },
//     heroBadge: {
//         display: "inline-block",
//         background: "rgba(99,102,241,0.2)",
//         border: "1px solid rgba(99,102,241,0.4)",
//         color: "#a5b4fc", borderRadius: "50px",
//         padding: "0.4rem 1.2rem", fontSize: "0.8rem",
//         fontWeight: "600", letterSpacing: "0.5px", marginBottom: "1.5rem",
//     },
//     heroTitle: {
//         fontSize: "clamp(2.2rem, 6vw, 4rem)",
//         fontWeight: "900", color: "#fff",
//         lineHeight: 1.15, margin: "0 0 1.5rem",
//         letterSpacing: "-1px",
//     },
//     heroAccent: {
//         background: "linear-gradient(90deg, #6366f1, #a78bfa)",
//         WebkitBackgroundClip: "text",
//         WebkitTextFillColor: "transparent",
//     },
//     heroSub: {
//         fontSize: "clamp(1rem, 2.5vw, 1.2rem)",
//         color: "rgba(255,255,255,0.7)",
//         maxWidth: "560px", lineHeight: 1.7, margin: "0 0 2.5rem",
//     },
//     heroCtas: {
//         display: "flex", gap: "1rem", flexWrap: "wrap", justifyContent: "center",
//         marginBottom: "4rem",
//     },
//     btnPrimary: {
//         padding: "0.9rem 2rem",
//         background: "linear-gradient(135deg, #6366f1, #4f46e5)",
//         color: "#fff", border: "none", borderRadius: "12px",
//         fontSize: "1rem", fontWeight: "700", cursor: "pointer",
//         boxShadow: "0 8px 24px rgba(99,102,241,0.4)",
//         transition: "transform 0.2s",
//     },
//     btnGhost: {
//         padding: "0.9rem 2rem",
//         background: "rgba(255,255,255,0.1)",
//         color: "#fff", border: "1px solid rgba(255,255,255,0.2)",
//         borderRadius: "12px", fontSize: "1rem",
//         fontWeight: "600", cursor: "pointer", textDecoration: "none",
//         display: "inline-flex", alignItems: "center",
//     },

//     // MOCKUP
//     mockupWrap: { position: "relative", display: "inline-block" },
//     mockCard: {
//         background: "#fff", borderRadius: "20px",
//         padding: "1.5rem", width: "280px",
//         boxShadow: "0 40px 80px rgba(0,0,0,0.5)",
//         position: "relative", zIndex: 1,
//     },
//     mockHeader: { display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "1rem" },
//     mockAvatar: {
//         width: "44px", height: "44px", borderRadius: "50%",
//         background: "linear-gradient(135deg, #1a1a2e, #0f3460)",
//         color: "#fff", display: "flex", alignItems: "center",
//         justifyContent: "center", fontSize: "0.9rem", fontWeight: "800",
//         flexShrink: 0,
//     },
//     mockName: { fontWeight: "800", fontSize: "0.95rem", color: "#1a1a2e" },
//     mockRole: { fontSize: "0.72rem", color: "#6b7280", marginTop: "2px" },
//     mockDivider: { height: "1px", background: "#f3f4f6", margin: "0.75rem 0" },
//     mockRow: {
//         display: "flex", gap: "0.5rem", alignItems: "center",
//         fontSize: "0.78rem", color: "#374151", padding: "3px 0",
//     },
//     mockQrArea: { textAlign: "center", marginTop: "0.5rem" },
//     mockQr: {
//         display: "inline-flex", flexDirection: "column", gap: "3px",
//         background: "#f9fafb", padding: "10px", borderRadius: "8px",
//         border: "1px solid #e5e7eb",
//     },
//     mockQrRow: { display: "flex", gap: "3px" },
//     mockQrCell: { width: "10px", height: "10px", borderRadius: "1px" },
//     mockQrLabel: { fontSize: "0.65rem", color: "#9ca3af", display: "block", marginTop: "6px" },
//     mockWalletBtn: {
//         marginTop: "0.75rem", background: "#0F9D58",
//         color: "#fff", borderRadius: "8px", padding: "0.5rem",
//         fontSize: "0.78rem", fontWeight: "700", textAlign: "center",
//     },
//     mockGlow: {
//         position: "absolute", top: "50%", left: "50%",
//         transform: "translate(-50%, -50%)",
//         width: "320px", height: "320px",
//         background: "radial-gradient(circle, rgba(99,102,241,0.3) 0%, transparent 70%)",
//         borderRadius: "50%", zIndex: 0,
//     },

//     // TRUST BAR
//     trustBar: {
//         background: "#fff", padding: "1.5rem 2rem",
//         display: "flex", flexDirection: "column", alignItems: "center", gap: "0.75rem",
//         borderBottom: "1px solid #f3f4f6",
//     },
//     trustLabel: { fontSize: "0.8rem", color: "#9ca3af", fontWeight: "600", textTransform: "uppercase", letterSpacing: "1px" },
//     trustIcons: { display: "flex", flexWrap: "wrap", gap: "1rem", justifyContent: "center", alignItems: "center" },
//     trustIcon: { fontSize: "0.9rem", fontWeight: "600", color: "#374151" },
//     trustDot: { color: "#d1d5db" },

//     // SECTIONS
//     section: {
//         padding: "5rem 1.5rem",
//         background: "#fff",
//     },
//     sectionInner: { maxWidth: "1100px", margin: "0 auto" },
//     sectionEyebrow: {
//         fontSize: "0.75rem", fontWeight: "700", color: "#6366f1",
//         textTransform: "uppercase", letterSpacing: "2px",
//         textAlign: "center", margin: "0 0 0.75rem",
//     },
//     sectionTitle: {
//         fontSize: "clamp(1.6rem, 4vw, 2.5rem)",
//         fontWeight: "900", textAlign: "center",
//         color: "#1a1a2e", margin: "0 0 1rem", lineHeight: 1.2,
//     },
//     sectionSub: {
//         fontSize: "1rem", color: "#6b7280", textAlign: "center",
//         maxWidth: "500px", margin: "0 auto 3rem", lineHeight: 1.7,
//     },

//     // STEPS
//     steps: {
//         display: "grid",
//         gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
//         gap: "1.5rem", marginTop: "3rem",
//     },
//     stepCard: {
//         background: "#fff", borderRadius: "20px",
//         padding: "2rem", border: "2px solid #f3f4f6",
//         transition: "all 0.3s", cursor: "default",
//     },
//     stepCardActive: {
//         border: "2px solid #6366f1",
//         boxShadow: "0 12px 40px rgba(99,102,241,0.15)",
//         transform: "translateY(-4px)",
//     },
//     stepNum: {
//         fontSize: "0.7rem", fontWeight: "800", color: "#6366f1",
//         letterSpacing: "2px", marginBottom: "1rem",
//     },
//     stepIcon: { fontSize: "2rem", marginBottom: "1rem" },
//     stepTitle: { fontSize: "1.1rem", fontWeight: "800", color: "#1a1a2e", margin: "0 0 0.5rem" },
//     stepDesc: { fontSize: "0.9rem", color: "#6b7280", lineHeight: 1.7, margin: 0 },

//     // FEATURES
//     featGrid: {
//         display: "grid",
//         gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
//         gap: "1.25rem", marginTop: "3rem",
//     },
//     featCard: {
//         background: "#fff", borderRadius: "16px",
//         padding: "1.5rem", border: "1.5px solid #e5e7eb",
//         transition: "box-shadow 0.2s",
//     },
//     featIcon: { fontSize: "1.8rem", marginBottom: "0.75rem" },
//     featTitle: { fontSize: "1rem", fontWeight: "800", color: "#1a1a2e", margin: "0 0 0.4rem" },
//     featDesc: { fontSize: "0.85rem", color: "#6b7280", lineHeight: 1.65, margin: 0 },

//     // USE CASES (kept for compatibility)
//     useCaseDesc: { fontSize: "0.85rem", color: "#6b7280", lineHeight: 1.65, margin: 0 },

//     // PRICING
//     pricingWrap: { display: "flex", justifyContent: "center", marginTop: "3rem" },
//     pricingCard: {
//         background: "#fff", borderRadius: "24px",
//         padding: "2.5rem 2rem", width: "100%", maxWidth: "380px",
//         border: "2px solid #6366f1",
//         boxShadow: "0 20px 60px rgba(99,102,241,0.15)",
//         textAlign: "center", position: "relative",
//     },
//     pricingBadge: {
//         position: "absolute", top: "-14px", left: "50%",
//         transform: "translateX(-50%)",
//         background: "linear-gradient(135deg, #6366f1, #4f46e5)",
//         color: "#fff", borderRadius: "50px",
//         padding: "0.3rem 1.2rem", fontSize: "0.75rem", fontWeight: "700",
//     },
//     pricingAmount: { fontSize: "3.5rem", fontWeight: "900", color: "#1a1a2e", lineHeight: 1 },
//     pricingPeriod: { fontSize: "0.9rem", color: "#6b7280", marginBottom: "2rem", marginTop: "0.25rem" },
//     pricingList: { listStyle: "none", padding: 0, margin: "0 0 2rem", textAlign: "left" },
//     pricingItem: {
//         display: "flex", alignItems: "center", gap: "0.6rem",
//         padding: "0.5rem 0", fontSize: "0.9rem", color: "#374151",
//         borderBottom: "1px solid #f3f4f6",
//     },
//     pricingCheck: { color: "#6366f1", fontWeight: "800" },

//     // CTA BANNER
//     ctaBanner: {
//         background: "linear-gradient(135deg, #0f0c29 0%, #1a1a2e 50%, #0f3460 100%)",
//         padding: "5rem 2rem", textAlign: "center",
//     },
//     ctaTitle: {
//         fontSize: "clamp(1.8rem, 5vw, 3rem)",
//         fontWeight: "900", color: "#fff", margin: "0 0 1rem",
//     },
//     ctaSub: {
//         fontSize: "1.1rem", color: "rgba(255,255,255,0.7)",
//         margin: "0 0 2.5rem",
//     },
//     ctaBtn: {
//         padding: "1rem 2.5rem",
//         background: "linear-gradient(135deg, #6366f1, #4f46e5)",
//         color: "#fff", border: "none", borderRadius: "14px",
//         fontSize: "1.05rem", fontWeight: "700", cursor: "pointer",
//         boxShadow: "0 8px 24px rgba(99,102,241,0.4)",
//     },

//     // FOOTER
//     footer: {
//         background: "#0f0c29", padding: "2.5rem 2rem",
//     },
//     footerInner: { textAlign: "center" },
//     footerLogo: {
//         fontSize: "1.1rem", fontWeight: "800", color: "#fff",
//         display: "inline-flex", alignItems: "center", gap: "6px",
//     },
//     footerTagline: { color: "rgba(255,255,255,0.5)", fontSize: "0.85rem", margin: "0.5rem 0 0" },
//     footerCopy: { color: "rgba(255,255,255,0.3)", fontSize: "0.8rem", marginTop: "0.5rem" },
// };





// import React, { useState, useEffect, useRef, useCallback } from "react";
// import { useNavigate } from "react-router-dom";

// const FONT_LINK = `
//   @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,500;0,600;0,700;1,400;1,600&family=Montserrat:wght@300;400;500;600;700;800&display=swap');
// `;

// const GLOBAL_CSS = `
// ${FONT_LINK}

// *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

// :root {
//   --black: #080808;
//   --dark: #0E0E0E;
//   --card: #141414;
//   --border: rgba(255,255,255,0.07);
//   --gold: #D4AF37;
//   --gold2: #F0D060;
//   --gold3: #C9A227;
//   --gold-dim: rgba(212,175,55,0.18);
//   --white: #FFFFFF;
//   --muted: rgba(255,255,255,0.45);
//   --muted2: rgba(255,255,255,0.25);
//   --serif: 'Cormorant Garamond', serif;
//   --sans: 'Montserrat', sans-serif;
//   --bg: #080808;
//   --surface: #141414;
//   --surface2: #1a1a1a;
//   --text-primary: #FFFFFF;
//   --text-muted: rgba(255,255,255,0.45);
//   --text-muted2: rgba(255,255,255,0.25);
//   --border-color: rgba(255,255,255,0.07);
//   --border-gold: rgba(212,175,55,0.2);
//   --gold-subtle: rgba(212,175,55,0.08);
//   --gold-subtle2: rgba(212,175,55,0.12);
// }

// /* ── LIGHT THEME ── */
// .lp-light {
//   --bg: #FFFFFF;
//   --surface: #F5F3EE;
//   --surface2: #EDE9E0;
//   --card: #F5F3EE;
//   --dark: #EDE9E0;
//   --black: #FFFFFF;
//   --border-color: rgba(0,0,0,0.08);
//   --border: rgba(0,0,0,0.08);
//   --border-gold: rgba(180,140,30,0.25);
//   --white: #0E0E0E;
//   --text-primary: #111111;
//   --text-muted: rgba(0,0,0,0.5);
//   --text-muted2: rgba(0,0,0,0.35);
//   --muted: rgba(0,0,0,0.5);
//   --muted2: rgba(0,0,0,0.35);
//   --gold: #A0780A;
//   --gold2: #C9991A;
//   --gold3: #8A6608;
//   --gold-dim: rgba(160,120,10,0.15);
//   --gold-subtle: rgba(160,120,10,0.08);
//   --gold-subtle2: rgba(160,120,10,0.12);
// }

// html { scroll-behavior: smooth; }
// body {
//   background: var(--bg);
//   color: var(--text-primary);
//   font-family: var(--sans);
//   overflow-x: hidden;
//   letter-spacing: 0.1px;
// }

// ::-webkit-scrollbar { width: 4px; }
// ::-webkit-scrollbar-track { background: var(--bg); }
// ::-webkit-scrollbar-thumb { background: rgba(212,175,55,0.3); border-radius: 2px; }

// /* ═══════════════════════════════════════
//    ENHANCEMENT 3 — DIRECTIONAL STAGGER REVEALS
//    Elements animate from different directions
// ═══════════════════════════════════════ */
// .reveal {
//   opacity: 0;
//   transition: opacity 0.75s cubic-bezier(0.22,1,0.36,1), transform 0.75s cubic-bezier(0.22,1,0.36,1);
// }
// .reveal.from-up    { transform: translateY(32px); }
// .reveal.from-down  { transform: translateY(-24px); }
// .reveal.from-left  { transform: translateX(-40px); }
// .reveal.from-right { transform: translateX(40px); }
// .reveal.from-scale { transform: scale(0.92); }

// /* stagger delays */
// .reveal.d1 { transition-delay: 0.05s; }
// .reveal.d2 { transition-delay: 0.13s; }
// .reveal.d3 { transition-delay: 0.21s; }
// .reveal.d4 { transition-delay: 0.29s; }
// .reveal.d5 { transition-delay: 0.37s; }
// .reveal.d6 { transition-delay: 0.45s; }

// .reveal.visible {
//   opacity: 1;
//   transform: translateY(0) translateX(0) scale(1);
// }

// /* ── NAV ── */
// .lp-nav {
//   position: fixed; top: 0; left: 0; right: 0; z-index: 200;
//   padding: 1.25rem 3rem; transition: all 0.4s;
// }
// .lp-nav.scrolled {
//   background: rgba(8,8,8,0.92);
//   backdrop-filter: blur(20px);
//   border-bottom: 0.5px solid rgba(212,175,55,0.12);
//   padding: 0.85rem 3rem;
// }
// .lp-light .lp-nav.scrolled {
//   background: rgba(255,255,255,0.95);
//   border-bottom: 0.5px solid rgba(160,120,10,0.18);
// }
// .lp-nav-inner {
//   max-width: 1180px; margin: 0 auto;
//   display: flex; align-items: center; justify-content: space-between;
// }
// .lp-logo {
//   display: flex; align-items: center; gap: 10px;
//   font-family: var(--sans); font-size: 15px; font-weight: 600;
//   color: var(--text-primary); text-decoration: none; letter-spacing: 0.2px;
//   flex-shrink: 0;
// }
// .lp-logo-icon { width: 34px; height: 34px; flex-shrink: 0; }
// .lp-logo em { color: var(--gold); font-style: normal; font-weight: 700; }

// /* desktop nav links */
// .lp-nav-links { display: flex; align-items: center; gap: 2.5rem; }
// .lp-nav-link { font-size: 13px; font-weight: 500; color: var(--text-muted); text-decoration: none; letter-spacing: 0.3px; transition: color 0.2s; }
// .lp-nav-link:hover { color: var(--text-primary); }
// .lp-nav-cta {
//   padding: 10px 22px;
//   background: linear-gradient(135deg, #D4AF37, #F0D060, #C9A227);
//   color: #0A0A0A; border: none; border-radius: 10px;
//   font-family: var(--sans); font-size: 13px; font-weight: 700;
//   letter-spacing: 0.8px; text-transform: uppercase;
//   cursor: pointer; position: relative; overflow: hidden;
//   transition: transform 0.15s, opacity 0.15s; white-space: nowrap;
// }
// .lp-nav-cta::before { content: ''; position: absolute; inset: 0; background: linear-gradient(135deg, rgba(255,255,255,0.2), transparent 60%); pointer-events: none; }
// .lp-nav-cta:hover { transform: translateY(-1px); opacity: 0.92; }

// /* ── HAMBURGER BUTTON ── */
// .lp-hamburger {
//   display: none;
//   flex-direction: column; justify-content: center; align-items: center;
//   gap: 5px;
//   width: 40px; height: 40px; border-radius: 10px;
//   border: 0.5px solid var(--border-gold);
//   background: var(--gold-subtle);
//   cursor: pointer; flex-shrink: 0;
//   transition: background 0.2s;
// }
// .lp-hamburger:hover { background: var(--gold-subtle2); }
// .lp-hamburger span {
//   display: block; width: 18px; height: 1.5px;
//   background: var(--gold); border-radius: 2px;
//   transition: all 0.3s cubic-bezier(0.22,1,0.36,1);
//   transform-origin: center;
// }
// .lp-hamburger.open span:nth-child(1) { transform: translateY(6.5px) rotate(45deg); }
// .lp-hamburger.open span:nth-child(2) { opacity: 0; transform: scaleX(0); }
// .lp-hamburger.open span:nth-child(3) { transform: translateY(-6.5px) rotate(-45deg); }

// /* ── MOBILE DRAWER MENU ── */
// .lp-mobile-menu {
//   position: fixed; top: 0; left: 0; right: 0; bottom: 0;
//   z-index: 190;
//   display: flex; flex-direction: column;
//   pointer-events: none;
// }
// /* backdrop */
// .lp-mobile-menu-backdrop {
//   position: absolute; inset: 0;
//   background: rgba(0,0,0,0.6);
//   backdrop-filter: blur(4px);
//   opacity: 0;
//   transition: opacity 0.35s ease;
// }
// .lp-mobile-menu.open .lp-mobile-menu-backdrop {
//   opacity: 1; pointer-events: all;
// }
// /* drawer panel */
// .lp-mobile-menu-panel {
//   position: absolute;
//   top: 0; right: 0;
//   width: min(320px, 85vw); height: 100%;
//   background: var(--bg);
//   border-left: 0.5px solid var(--border-gold);
//   display: flex; flex-direction: column;
//   padding: 0;
//   transform: translateX(100%);
//   transition: transform 0.4s cubic-bezier(0.22,1,0.36,1);
//   box-shadow: -20px 0 60px rgba(0,0,0,0.4);
//   pointer-events: none;
//   overflow: hidden;
// }
// .lp-mobile-menu.open .lp-mobile-menu-panel {
//   transform: translateX(0); pointer-events: all;
// }
// /* top bar inside drawer */
// .lp-mobile-menu-header {
//   display: flex; align-items: center; justify-content: space-between;
//   padding: 1.25rem 1.5rem;
//   border-bottom: 0.5px solid var(--border-color);
// }
// .lp-mobile-close {
//   width: 36px; height: 36px; border-radius: 9px;
//   border: 0.5px solid var(--border-color);
//   background: var(--surface);
//   display: flex; align-items: center; justify-content: center;
//   cursor: pointer; color: var(--text-muted); font-size: 16px;
//   transition: background 0.2s, color 0.2s;
// }
// .lp-mobile-close:hover { background: var(--surface2); color: var(--text-primary); }
// /* nav links inside drawer */
// .lp-mobile-links {
//   flex: 1; display: flex; flex-direction: column;
//   padding: 1.5rem;
//   gap: 0.25rem;
// }
// .lp-mobile-link {
//   display: flex; align-items: center; gap: 12px;
//   padding: 1rem 1.25rem;
//   border-radius: 14px;
//   font-family: var(--sans); font-size: 15px; font-weight: 600;
//   color: var(--text-muted); text-decoration: none;
//   border: 0.5px solid transparent;
//   transition: all 0.2s;
//   opacity: 0; transform: translateX(20px);
//   transition: background 0.2s, color 0.2s, border-color 0.2s, opacity 0.35s ease, transform 0.35s cubic-bezier(0.22,1,0.36,1);
// }
// .lp-mobile-link:nth-child(1) { transition-delay: 0.06s; }
// .lp-mobile-link:nth-child(2) { transition-delay: 0.11s; }
// .lp-mobile-link:nth-child(3) { transition-delay: 0.16s; }
// .lp-mobile-menu.open .lp-mobile-link { opacity: 1; transform: translateX(0); }
// .lp-mobile-link:hover { background: var(--surface2); color: var(--text-primary); border-color: var(--border-gold); }
// .lp-mobile-link-icon { font-size: 16px; width: 20px; text-align: center; }
// /* gold accent line */
// .lp-mobile-divider { height: 0.5px; background: linear-gradient(90deg, transparent, var(--border-gold), transparent); margin: 0.5rem 1.5rem; }
// /* CTA inside drawer */
// .lp-mobile-menu-footer {
//   padding: 1.5rem;
//   border-top: 0.5px solid var(--border-color);
//   display: flex; flex-direction: column; gap: 0.75rem;
//   opacity: 0; transform: translateY(12px);
//   transition: opacity 0.35s ease 0.22s, transform 0.35s ease 0.22s;
// }
// .lp-mobile-menu.open .lp-mobile-menu-footer { opacity: 1; transform: translateY(0); }
// .lp-mobile-cta {
//   width: 100%; padding: 14px;
//   background: linear-gradient(135deg, #D4AF37, #F0D060, #C9A227);
//   color: #0A0A0A; border: none; border-radius: 12px;
//   font-family: var(--sans); font-size: 14px; font-weight: 700;
//   letter-spacing: 0.8px; text-transform: uppercase; cursor: pointer;
//   position: relative; overflow: hidden;
// }
// .lp-mobile-cta::before { content:''; position:absolute; inset:0; background:linear-gradient(135deg,rgba(255,255,255,0.2),transparent 60%); pointer-events:none; }
// .lp-mobile-tagline {
//   text-align: center; font-size: 11px; font-weight: 500;
//   color: var(--text-muted2); letter-spacing: 0.5px;
// }

// /* trust bar — desktop override: horizontal single row */
// @media (min-width: 901px) {
//   .lp-trust {
//     flex-direction: row;
//     padding: 1.5rem 3rem;
//     gap: 2.5rem;
//   }
//   .lp-trust-items { width: auto; max-width: none; gap: 0; }
//   .lp-trust-item {
//     flex-direction: row; flex: none;
//     padding: 0 1.5rem;
//     border-right: 0.5px solid var(--border-color);
//     border-bottom: none; gap: 8px;
//   }
//   .lp-trust-item:last-child { border-right: none; }
//   .lp-trust-item-icon { width:28px; height:28px; border-radius:8px; font-size:1rem; flex-shrink:0; margin-bottom:0; }
// }

// /* mobile right cluster — hidden on desktop, shown on mobile via media query */
// .lp-mobile-cluster {
//   display: none;
//   align-items: center; gap: 10px;
// }
// .lp-theme-btn-mobile { display: flex; }
// /* desktop: hide mobile cluster */
// @media (min-width: 901px) {
//   .lp-mobile-cluster { display: none !important; }
// }

// /* ── HERO ── */
// .lp-hero {
//   min-height: 100vh;
//   display: grid; grid-template-columns: 1fr 1fr;
//   align-items: center; gap: 4rem;
//   max-width: 1180px; margin: 0 auto;
//   padding: 10rem 3rem 3rem; position: relative;
// }
// .lp-hero-left { position: relative; z-index: 2; }
// .lp-hero-eyebrow {
//   display: inline-flex; align-items: center; gap: 8px;
//   padding: 6px 14px;
//   background: var(--gold-subtle2); border: 0.5px solid var(--border-gold);
//   border-radius: 100px; font-size: 11px; font-weight: 700; color: var(--gold);
//   letter-spacing: 1.5px; text-transform: uppercase; margin-bottom: 2rem;
// }
// .lp-hero-eyebrow-dot { width: 5px; height: 5px; border-radius: 50%; background: var(--gold); animation: pulse-gold 2s ease-in-out infinite; }
// @keyframes pulse-gold { 0%,100%{opacity:1;transform:scale(1);} 50%{opacity:0.4;transform:scale(0.7);} }

// .lp-hero-title { font-family: var(--serif); font-size: clamp(3.2rem, 5.8vw, 5.3rem); font-weight: 600; line-height: 1.02; letter-spacing: -0.8px; color: var(--text-primary); margin-bottom: 1.75rem; }
// .lp-hero-title em { font-style: italic; font-weight: 600; color: var(--gold); }
// .lp-hero-sub { font-size: 15px; font-weight: 400; color: var(--text-muted); line-height: 1.9; max-width: 470px; margin-bottom: 1rem; }
// .lp-hero-ctas { display: flex; gap: 1rem; flex-wrap: wrap; }

// .lp-btn-gold {
//   padding: 16px 32px;
//   background: linear-gradient(135deg, #D4AF37, #F0D060, #C9A227);
//   color: #0A0A0A; border: none; border-radius: 12px;
//   font-family: var(--sans); font-size: 13px; font-weight: 700;
//   letter-spacing: 1px; text-transform: uppercase;
//   cursor: pointer; position: relative; overflow: hidden;
//   transition: transform 0.2s, box-shadow 0.2s;
//   display: flex; align-items: center; gap: 8px;
// }
// .lp-btn-gold::before { content:''; position:absolute; inset:0; background:linear-gradient(135deg,rgba(255,255,255,0.25),transparent 60%); pointer-events:none; }
// .lp-btn-gold:hover { transform: translateY(-2px); box-shadow: 0 8px 28px rgba(212,175,55,0.3); }

// .lp-btn-ghost {
//   padding: 16px 28px; background: transparent;
//   color: var(--text-muted); border: 0.5px solid var(--border-color);
//   border-radius: 12px; font-family: var(--sans); font-size: 13px; font-weight: 600;
//   letter-spacing: 0.5px; cursor: pointer; text-decoration: none;
//   display: inline-flex; align-items: center; gap: 8px;
//   transition: border-color 0.2s, color 0.2s;
// }
// .lp-btn-ghost:hover { border-color: var(--border-gold); color: var(--text-primary); }

// /* ═══════════════════════════════════════
//    ENHANCEMENT 1 — COUNT-UP STATS
// ═══════════════════════════════════════ */
// .lp-hero-stats { display: flex; gap: 2.5rem; padding-bottom: 1.5rem; margin-bottom: 1.5rem; }
// .lp-stat { position: relative; }
// .lp-stat-num {
//   font-family: var(--serif); font-size: 2.4rem; font-weight: 600;
//   color: var(--gold); line-height: 1;
//   /* subtle gold shimmer when counting */
// }
// .lp-stat-num.counting {
//   animation: stat-shimmer 0.1s ease infinite alternate;
// }
// @keyframes stat-shimmer {
//   from { opacity: 0.7; }
//   to   { opacity: 1; }
// }
// .lp-stat-label { font-size: 11px; font-weight: 500; color: var(--text-muted2); letter-spacing: 0.5px; margin-top: 5px; }
// /* thin gold underline that fills as number counts */
// .lp-stat-bar {
//   height: 1.5px; background: var(--border-gold); margin-top: 8px;
//   border-radius: 1px; overflow: hidden;
// }
// .lp-stat-bar-fill {
//   height: 100%;
//   background: linear-gradient(90deg, var(--gold), var(--gold2));
//   border-radius: 1px;
//   width: 0%;
//   transition: width 0.05s linear;
// }

// /* ── HERO RIGHT ── */
// .lp-hero-right { position: relative; display: flex; align-items: center; justify-content: center; z-index: 2; }
// .scan-scene { position: relative; width: 380px; height: 480px; }

// /* ═══════════════════════════════════════
//    ENHANCEMENT 5 — HERO RIPPLE / SUCCESS
// ═══════════════════════════════════════ */
// /* Ripple rings that emit from phone B when scan "completes" */
// .scan-ripple {
//   position: absolute;
//   right: -10px; top: 50px;
//   width: 60px; height: 60px;
//   border-radius: 50%;
//   border: 1.5px solid rgba(212,175,55,0);
//   z-index: 10;
//   pointer-events: none;
// }
// .scan-ripple.r1 { animation: ripple-out 2.5s ease-out infinite; animation-delay: 3.2s; }
// .scan-ripple.r2 { animation: ripple-out 2.5s ease-out infinite; animation-delay: 3.6s; }
// .scan-ripple.r3 { animation: ripple-out 2.5s ease-out infinite; animation-delay: 4.0s; }
// @keyframes ripple-out {
//   0%   { transform: scale(1); border-color: rgba(212,175,55,0.6); opacity: 1; }
//   100% { transform: scale(3.5); border-color: rgba(212,175,55,0); opacity: 0; }
// }

// /* Success ping badge */
// .scan-success {
//   position: absolute;
//   right: 8px; top: 38px;
//   background: linear-gradient(135deg, #D4AF37, #F0D060);
//   color: #0A0A0A;
//   border-radius: 100px;
//   padding: 4px 10px 4px 8px;
//   font-family: var(--sans); font-size: 9px; font-weight: 700;
//   letter-spacing: 0.5px;
//   display: flex; align-items: center; gap: 5px;
//   z-index: 12; white-space: nowrap;
//   opacity: 0;
//   animation: success-pop 0.5s cubic-bezier(0.34,1.56,0.64,1) forwards;
//   animation-delay: 3.1s;
//   box-shadow: 0 4px 16px rgba(212,175,55,0.4);
// }
// .scan-success-dot { width: 6px; height: 6px; border-radius: 50%; background: #0A0A0A; }
// @keyframes success-pop {
//   0%   { opacity: 0; transform: scale(0.5) translateY(6px); }
//   100% { opacity: 1; transform: scale(1) translateY(0); }
// }

// /* Phone A */
// .phone-scanner {
//   position: absolute; left: 0; top: 60px;
//   width: 155px; height: 270px; background: #1A1A1A;
//   border-radius: 22px; border: 1px solid rgba(255,255,255,0.1);
//   overflow: hidden; box-shadow: 0 24px 60px rgba(0,0,0,0.6);
//   z-index: 3;
//   animation: phone-a-intro 0.8s cubic-bezier(0.34,1.56,0.64,1) both; animation-delay: 0.3s;
// }
// @keyframes phone-a-intro { from{opacity:0;transform:translateX(-30px) rotate(-6deg);} to{opacity:1;transform:translateX(0) rotate(-6deg);} }
// .phone-scanner-screen { width:100%; height:100%; background:#111; display:flex; flex-direction:column; align-items:center; justify-content:center; gap:12px; padding:12px; }
// .scanner-viewfinder { width:100px; height:100px; border-radius:10px; position:relative; background:rgba(212,175,55,0.04); }
// .scanner-viewfinder::before,.scanner-viewfinder::after { content:''; position:absolute; width:20px; height:20px; border-color:#D4AF37; border-style:solid; }
// .scanner-viewfinder::before { top:0;left:0;border-width:2px 0 0 2px;border-radius:6px 0 0 0; }
// .scanner-viewfinder::after  { bottom:0;right:0;border-width:0 2px 2px 0;border-radius:0 0 6px 0; }
// .vf-corner-tr { position:absolute;top:0;right:0;width:20px;height:20px;border-top:2px solid #D4AF37;border-right:2px solid #D4AF37;border-radius:0 6px 0 0; }
// .vf-corner-bl { position:absolute;bottom:0;left:0;width:20px;height:20px;border-bottom:2px solid #D4AF37;border-left:2px solid #D4AF37;border-radius:0 0 0 6px; }
// .scan-laser { position:absolute;left:4px;right:4px;height:2px;background:linear-gradient(90deg,transparent,#D4AF37,transparent);border-radius:1px;top:4px;animation:laser-sweep 2s ease-in-out infinite;animation-delay:1.2s; }
// @keyframes laser-sweep { 0%{top:8px;opacity:0;} 10%{opacity:1;} 90%{opacity:1;} 100%{top:88px;opacity:0;} }

// /* scanner screen flashes green briefly on scan success */
// .scanner-success-flash {
//   position: absolute; inset: 0;
//   background: rgba(212,175,55,0.12);
//   opacity: 0;
//   animation: flash-once 0.4s ease forwards;
//   animation-delay: 3.0s;
//   pointer-events: none; z-index: 10;
// }
// @keyframes flash-once { 0%{opacity:0;} 40%{opacity:1;} 100%{opacity:0;} }

// .scanner-label { font-size:9px;font-weight:600;color:rgba(255,255,255,0.4);letter-spacing:1px;text-align:center;text-transform:uppercase; }
// .mini-qr { position:absolute;inset:10px;display:grid;grid-template-columns:repeat(6,1fr);grid-template-rows:repeat(6,1fr);gap:1.5px;opacity:0.6; }
// .mini-qr-cell { border-radius:1px; }

// /* Phone B */
// .phone-profile {
//   position:absolute;right:0;top:0;width:175px;height:310px;background:#0E0E0E;
//   border-radius:24px;border:1px solid rgba(212,175,55,0.2);overflow:hidden;
//   box-shadow:0 32px 80px rgba(0,0,0,0.7),0 0 0 1px rgba(212,175,55,0.08);
//   z-index:4;
//   animation:phone-b-intro 0.8s cubic-bezier(0.34,1.56,0.64,1) both;animation-delay:0.5s;
// }
// @keyframes phone-b-intro { from{opacity:0;transform:translateY(30px) rotate(4deg);} to{opacity:1;transform:translateY(0) rotate(4deg);} }
// .profile-card-wrap { width:100%;height:100%;animation:card-reveal 0.7s cubic-bezier(0.22,1,0.36,1) both;animation-delay:2s; }
// @keyframes card-reveal { from{transform:translateY(40px);opacity:0;} to{transform:translateY(0);opacity:1;} }
// .profile-banner { width:100%;height:110px;background:linear-gradient(160deg,#1a1a1a,#2a2020);position:relative;overflow:hidden; }
// .profile-banner-img { width:100%;height:100%;background:linear-gradient(160deg,#1e1a10 0%,#2a2010 50%,#181818 100%); }
// .profile-banner-fade { position:absolute;bottom:0;left:0;right:0;height:50px;background:linear-gradient(transparent,#0E0E0E); }
// .profile-avatar-ring { position:absolute;bottom:2px;left:14px;width:44px;height:44px;border-radius:50%;border:2px solid #0E0E0E;background:#222;overflow:hidden;box-shadow:0 0 0 1.5px rgba(212,175,55,0.5);animation:avatar-pop 0.4s cubic-bezier(0.34,1.56,0.64,1) both;animation-delay:2.4s; }
// @keyframes avatar-pop { from{transform:scale(0.3);opacity:0;} to{transform:scale(1);opacity:1;} }
// .profile-avatar-inner { width:100%;height:100%;background:linear-gradient(145deg,#2a2010,#3a3020);display:flex;align-items:center;justify-content:center;font-family:'Cormorant Garamond',serif;font-size:14px;font-weight:600;color:rgba(212,175,55,0.8);letter-spacing:1px; }
// .profile-info { padding:28px 14px 12px; }
// .profile-info-name { font-family:'Cormorant Garamond',serif;font-size:16px;font-weight:600;color:#FFF;line-height:1.2;margin-bottom:2px; }
// .profile-info-name em { font-style:italic;color:#D4AF37; }
// .profile-info-role { font-size:8px;font-weight:600;color:rgba(255,255,255,0.45);letter-spacing:0.3px;margin-bottom:10px; }
// .profile-tiles { display:grid;grid-template-columns:1fr 1fr 1fr;gap:5px;margin-bottom:8px; }
// .profile-tile { background:#1A1A1A;border:0.5px solid rgba(255,255,255,0.06);border-radius:8px;padding:7px 6px;display:flex;flex-direction:column;gap:4px;animation:tile-slide 0.3s ease both; }
// .profile-tile:nth-child(1){animation-delay:2.5s;} .profile-tile:nth-child(2){animation-delay:2.65s;} .profile-tile:nth-child(3){animation-delay:2.8s;}
// @keyframes tile-slide { from{opacity:0;transform:translateY(6px);} to{opacity:1;transform:translateY(0);} }
// .tile-dot { width:12px;height:12px;border-radius:50%;background:rgba(212,175,55,0.15);display:flex;align-items:center;justify-content:center; }
// .tile-dot-inner { width:5px;height:5px;border-radius:50%;background:#D4AF37; }
// .tile-text { font-size:7px;color:rgba(255,255,255,0.4);line-height:1.3;font-weight:500; }
// .profile-save-btn { width:100%;padding:8px;background:linear-gradient(135deg,#D4AF37,#F0D060);color:#0A0A0A;border:none;border-radius:8px;font-size:8px;font-weight:700;letter-spacing:0.8px;text-transform:uppercase;cursor:default;display:flex;align-items:center;justify-content:center;gap:4px;animation:tile-slide 0.3s ease both;animation-delay:2.95s; }

// .beam-line { position:absolute;top:165px;left:148px;width:90px;height:1px;background:linear-gradient(90deg,#D4AF37,rgba(212,175,55,0));transform-origin:left center;animation:beam-appear 0.4s ease both;animation-delay:1.5s;z-index:5; }
// @keyframes beam-appear { from{opacity:0;} to{opacity:1;} }
// .beam-particle { position:absolute;top:160px;left:148px;width:6px;height:6px;border-radius:50%;background:#D4AF37;animation:particle-travel 2s ease-in-out infinite;animation-delay:1.6s;z-index:6; }
// @keyframes particle-travel { 0%{left:148px;opacity:0;transform:scale(0.5);} 10%{opacity:1;transform:scale(1);} 90%{opacity:1;} 100%{left:228px;opacity:0;transform:scale(0.5);} }
// .hero-orb-1 { position:absolute;width:500px;height:500px;border-radius:50%;background:radial-gradient(circle,rgba(212,175,55,0.06) 0%,transparent 65%);top:-100px;right:-100px;pointer-events:none;z-index:0; }

// /* ── SECTION SHARED ── */
// .lp-section { padding: 5rem 3rem; position: relative; }
// .lp-section-inner { max-width: 1180px; margin: 0 auto; }
// .lp-eyebrow { display:block;font-size:10px;font-weight:700;letter-spacing:3px;text-transform:uppercase;color:var(--gold);margin-bottom:1.25rem;text-align:center; }
// .lp-section-title { font-family:var(--serif);font-size:clamp(2.3rem,4vw,3.5rem);font-weight:600;line-height:1.08;letter-spacing:-0.4px;color:var(--text-primary);text-align:center;margin-bottom:1.25rem; }
// .lp-section-title em { font-style:italic;color:var(--gold); }
// .lp-section-sub { font-size:15px;font-weight:400;color:var(--text-muted);text-align:center;max-width:520px;margin:0 auto 4rem;line-height:1.9; }

// .gold-rule { height:0.5px;background:linear-gradient(90deg,transparent,rgba(212,175,55,0.3),transparent); }
// .lp-light .gold-rule { background:linear-gradient(90deg,transparent,rgba(160,120,10,0.2),transparent); }

// /* ── TRUST BAR ── */
// .lp-trust {
//   padding: 1.5rem 2rem;
//   display: flex; flex-direction: column; align-items: center;
//   gap: 1rem; background: var(--surface);
// }
// .lp-trust-label {
//   font-size: 10px; font-weight: 600; color: var(--text-muted2);
//   letter-spacing: 2.5px; text-transform: uppercase;
// }
// .lp-trust-items {
//   display: flex; align-items: stretch;
//   justify-content: center;
//   gap: 0; flex-wrap: nowrap; width: 100%; max-width: 560px;
// }
// .lp-trust-item {
//   display: flex; flex-direction: column; align-items: center; gap: 5px;
//   flex: 1; padding: 0.6rem 0.5rem;
//   border-right: 0.5px solid var(--border-color);
//   font-size: 11px; font-weight: 600; color: var(--text-muted);
//   letter-spacing: 0.2px; text-align: center;
// }
// .lp-trust-item:last-child { border-right: none; }
// .lp-trust-item-icon {
//   font-size: 1.25rem; line-height: 1;
//   width: 36px; height: 36px; border-radius: 10px;
//   background: var(--gold-subtle); border: 0.5px solid var(--border-gold);
//   display: flex; align-items: center; justify-content: center;
//   margin-bottom: 2px;
// }
// .lp-trust-sep { display: none; }

// /* ═══════════════════════════════════════
//    ENHANCEMENT 2 — PROGRESS CONNECTOR
//    The SVG arrow between steps fills on scroll
// ═══════════════════════════════════════ */
// .lp-how-timeline {
//   display: grid;
//   grid-template-columns: 1fr 72px 1fr 72px 1fr;
//   align-items: start; gap: 0; margin-top: 1rem;
// }
// .lp-how-bridge {
//   display: flex; align-items: flex-start; justify-content: center; padding-top: 2.6rem;
// }
// /* The SVG path for the arrow */
// .how-arrow-path {
//   stroke: var(--border-gold);
//   stroke-dasharray: 60;
//   stroke-dashoffset: 60;
//   transition: stroke-dashoffset 0.9s cubic-bezier(0.22,1,0.36,1), stroke 0.4s ease;
// }
// .how-arrow-path.filled {
//   stroke-dashoffset: 0;
//   stroke: var(--gold);
// }
// /* arrowhead */
// .how-arrowhead {
//   fill: var(--border-gold);
//   transition: fill 0.4s ease 0.7s;
// }
// .how-arrow-path.filled + .how-arrowhead,
// .how-arrowhead.filled {
//   fill: var(--gold);
// }

// .lp-how-card {
//   background: var(--surface);
//   border: 0.5px solid var(--border-color);
//   border-radius: 20px; padding: 2.2rem 1.8rem 2rem;
//   position: relative; overflow: hidden;
//   transition: border-color 0.3s, transform 0.3s;
//   cursor: default;
// }
// .lp-how-card::before { content:'';position:absolute;inset:0;background:radial-gradient(ellipse 80% 60% at 50% -10%,var(--gold-subtle2) 0%,transparent 70%);opacity:0;transition:opacity 0.3s; }
// .lp-how-card:hover { border-color: var(--border-gold); transform: translateY(-4px); }
// .lp-how-card:hover::before { opacity: 1; }
// .lp-how-num { font-family:var(--serif);font-size:7rem;font-weight:700;color:var(--gold-subtle2);line-height:1;position:absolute;bottom:-0.5rem;right:0.8rem;letter-spacing:-3px;pointer-events:none;user-select:none;opacity:0.6; }
// .lp-light .lp-how-num { opacity:1;color:rgba(160,120,10,0.1); }
// .lp-how-icon-ring { width:56px;height:56px;border-radius:16px;background:var(--gold-subtle);border:1px solid var(--border-gold);display:flex;align-items:center;justify-content:center;font-size:1.6rem;margin-bottom:1.5rem;position:relative; }
// .lp-how-icon-ring::after { content:'';position:absolute;inset:-4px;border-radius:20px;border:1px dashed var(--border-gold);animation:lp-spin-slow 12s linear infinite; }
// @keyframes lp-spin-slow { to{transform:rotate(360deg);} }
// .lp-how-step-label { font-size:9px;font-weight:700;letter-spacing:2.5px;text-transform:uppercase;color:var(--gold);margin-bottom:0.6rem; }
// .lp-how-title { font-family:var(--serif);font-size:1.65rem;font-weight:600;color:var(--text-primary);line-height:1.15;margin-bottom:0.75rem; }
// .lp-how-desc { font-size:13.5px;color:var(--text-muted);line-height:1.8; }

// /* ═══════════════════════════════════════
//    ENHANCEMENT 4 — BENTO SHIMMER + SOCIAL PILL STAGGER
// ═══════════════════════════════════════ */
// .lp-feat-bento { display:grid;grid-template-columns:repeat(4,1fr);grid-template-rows:auto auto;gap:10px;margin-top:1rem; }

// .lp-feat-bcard {
//   background: var(--surface);
//   border: 0.5px solid var(--border-color);
//   border-radius: 18px; padding: 1.8rem 1.6rem;
//   position: relative; overflow: hidden;
//   transition: border-color 0.3s, transform 0.25s, background 0.3s;
//   cursor: default;
// }
// /* shimmer sweep on hover */
// .lp-feat-bcard::before {
//   content: '';
//   position: absolute;
//   top: 0; left: -100%; width: 60%; height: 100%;
//   background: linear-gradient(105deg, transparent, rgba(212,175,55,0.06), transparent);
//   transition: left 0s;
//   pointer-events: none; z-index: 1;
// }
// .lp-feat-bcard:hover::before {
//   left: 160%;
//   transition: left 0.6s ease;
// }
// .lp-feat-bcard:hover { border-color:var(--border-gold); background:var(--surface2); transform:translateY(-3px); }
// .lp-feat-bcard.hero { grid-column:span 2; background:var(--surface2); border-color:var(--border-gold); padding:2.4rem 2.2rem; }
// .lp-feat-bcard.tall { grid-row:span 2; display:flex; flex-direction:column; }
// .lp-feat-bcard::after { content:'';position:absolute;top:0;left:0;right:0;height:1px;background:transparent;transition:background 0.3s; }
// .lp-feat-bcard:hover::after,.lp-feat-bcard.hero::after { background:linear-gradient(90deg,transparent,var(--border-gold),transparent); }

// .lp-feat-bicon { width:44px;height:44px;border-radius:12px;background:var(--gold-subtle);border:0.5px solid var(--border-gold);display:flex;align-items:center;justify-content:center;font-size:1.3rem;margin-bottom:1.25rem;flex-shrink:0; }
// .lp-feat-bcard.hero .lp-feat-bicon { width:52px;height:52px;font-size:1.6rem;border-radius:14px; }
// .lp-feat-btag { display:inline-block;font-size:8.5px;font-weight:700;letter-spacing:2px;text-transform:uppercase;color:var(--gold);margin-bottom:0.5rem;opacity:0.8; }
// .lp-feat-btitle { font-family:var(--serif);font-size:1.3rem;font-weight:600;color:var(--text-primary);line-height:1.15;margin-bottom:0.5rem; }
// .lp-feat-bcard.hero .lp-feat-btitle { font-size:1.65rem; }
// .lp-feat-bdesc { font-size:13px;color:var(--text-muted);line-height:1.8;flex:1; }

// .lp-feat-wallet { display:flex;gap:6px;margin-top:1.25rem; }
// .lp-wallet-chip { height:26px;border-radius:6px;flex:1;background:var(--gold-subtle);border:0.5px solid var(--border-gold);display:flex;align-items:center;justify-content:center;font-size:9px;font-weight:600;color:var(--gold);letter-spacing:0.5px;position:relative;overflow:hidden; }
// /* wallet chip shimmer */
// .lp-wallet-chip::after { content:'';position:absolute;top:0;left:-100%;width:50%;height:100%;background:linear-gradient(90deg,transparent,rgba(212,175,55,0.2),transparent);animation:chip-shine 3s ease-in-out infinite; }
// .lp-wallet-chip:nth-child(2)::after { animation-delay:0.4s; }
// .lp-wallet-chip:nth-child(3)::after { animation-delay:0.8s; }
// @keyframes chip-shine { 0%,70%{left:-100%;} 100%{left:200%;} }

// /* social pills stagger in when card enters viewport */
// .lp-feat-socials { display:flex;flex-wrap:wrap;gap:6px;margin-top:1.1rem; }
// .lp-social-pill {
//   padding: 4px 10px;
//   background: var(--gold-subtle); border: 0.5px solid var(--border-gold);
//   border-radius: 100px; font-size: 10px; font-weight: 500; color: var(--text-muted);
//   opacity: 0; transform: translateY(6px) scale(0.94);
//   transition: opacity 0.35s ease, transform 0.35s ease, background 0.2s, color 0.2s;
// }
// .lp-social-pill:hover { background: var(--gold-subtle2); color: var(--gold); }
// /* stagger delays via nth-child */
// .lp-social-pill:nth-child(1) { transition-delay: 0.05s; }
// .lp-social-pill:nth-child(2) { transition-delay: 0.12s; }
// .lp-social-pill:nth-child(3) { transition-delay: 0.19s; }
// .lp-social-pill:nth-child(4) { transition-delay: 0.26s; }
// .lp-social-pill:nth-child(5) { transition-delay: 0.33s; }

// /* pills animate in when parent card is visible */
// .lp-feat-bcard.pills-visible .lp-social-pill {
//   opacity: 1; transform: translateY(0) scale(1);
// }

// .lp-qr-art { margin-top:auto;padding-top:1.5rem;display:grid;grid-template-columns:repeat(5,1fr);grid-template-rows:repeat(5,1fr);gap:3px;opacity:0.2;transition:opacity 0.3s; }
// .lp-feat-bcard.tall:hover .lp-qr-art { opacity:0.45; }
// .lp-qr-cell { border-radius:2px; }

// /* ── USE CASES ── */
// .lp-use-tabs { display:flex;flex-wrap:wrap;gap:0.5rem;justify-content:center;margin-bottom:3rem; }
// .lp-use-tab { padding:8px 18px;border-radius:100px;font-size:13px;font-weight:600;cursor:pointer;transition:all 0.2s;font-family:var(--sans);display:flex;align-items:center;gap:6px; }
// .lp-use-panel { display:grid;grid-template-columns:1fr 1fr;gap:3rem;align-items:center;background:var(--surface);border:0.5px solid var(--border-color);border-radius:24px;padding:3rem;position:relative;overflow:hidden; }
// .lp-use-panel::before { content:'';position:absolute;top:0;left:0;right:0;height:1px;background:linear-gradient(90deg,transparent,var(--border-gold),transparent); }
// .lp-use-badge { display:inline-flex;align-items:center;gap:8px;padding:6px 14px;border-radius:100px;margin-bottom:1.5rem;font-size:12px;font-weight:700;background:var(--gold-subtle);color:var(--gold);border:0.5px solid var(--border-gold); }
// .lp-use-tagline { font-family:var(--serif);font-size:2.2rem;font-weight:600;color:var(--text-primary);line-height:1.1;margin-bottom:1rem; }
// .lp-use-desc { font-size:14px;font-weight:400;color:var(--text-muted);line-height:1.9;margin-bottom:2rem; }

// /* use case panel transition on tab switch */
// .lp-use-content { animation: tab-fade 0.35s cubic-bezier(0.22,1,0.36,1) both; }
// @keyframes tab-fade { from{opacity:0;transform:translateY(10px);} to{opacity:1;transform:translateY(0);} }

// .lp-use-highlights { display:flex;flex-direction:column;gap:0.75rem; }
// .lp-use-hl { display:flex;align-items:center;gap:12px;background:var(--surface2);border:0.5px solid var(--border-color);border-radius:12px;padding:1rem 1.25rem;animation:hl-slide 0.3s ease both; }
// .lp-use-hl:nth-child(1){animation-delay:0.05s;} .lp-use-hl:nth-child(2){animation-delay:0.12s;} .lp-use-hl:nth-child(3){animation-delay:0.19s;} .lp-use-hl:nth-child(4){animation-delay:0.26s;}
// @keyframes hl-slide { from{opacity:0;transform:translateX(12px);} to{opacity:1;transform:translateX(0);} }
// .lp-use-hl-check { width:28px;height:28px;border-radius:50%;display:flex;align-items:center;justify-content:center;flex-shrink:0;font-size:11px;font-weight:700;background:var(--gold-subtle);color:var(--gold); }
// .lp-use-hl-text { font-size:13px;font-weight:500;color:var(--text-muted); }

// /* ── PRICING + CTA MERGED ── */
// .lp-pricing-cta { padding:5rem 3rem;position:relative;overflow:hidden; }
// .lp-pricing-cta::before { content:'';position:absolute;inset:0;background:radial-gradient(ellipse 70% 80% at 50% 50%,var(--gold-subtle) 0%,transparent 70%);pointer-events:none; }
// .lp-pricing-cta-inner { max-width:1180px;margin:0 auto;display:grid;grid-template-columns:1fr 1.1fr;gap:4rem;align-items:center;position:relative; }
// .lp-pricing-eyebrow { display:block;font-size:10px;font-weight:700;letter-spacing:3px;text-transform:uppercase;color:var(--gold);margin-bottom:1.25rem; }
// .lp-pricing-headline { font-family:var(--serif);font-size:clamp(2.8rem,5vw,4.2rem);font-weight:600;line-height:1.05;color:var(--text-primary);margin-bottom:1.25rem;letter-spacing:-0.5px; }
// .lp-pricing-headline em { font-style:italic;color:var(--gold); }
// .lp-pricing-sub { font-size:15px;color:var(--text-muted);line-height:1.9;margin-bottom:2.5rem;max-width:400px; }
// .lp-pricing-perks { display:flex;flex-direction:column;gap:0.6rem;margin-bottom:2.5rem; }
// .lp-pricing-perk { display:flex;align-items:center;gap:10px;font-size:14px;font-weight:500;color:var(--text-muted); }
// .lp-perk-tick { width:20px;height:20px;border-radius:50%;flex-shrink:0;background:var(--gold-subtle);border:0.5px solid var(--border-gold);display:flex;align-items:center;justify-content:center;font-size:9px;color:var(--gold);font-weight:700; }

// .lp-price-card { background:var(--surface);border:0.5px solid var(--border-gold);border-radius:24px;padding:2.5rem;position:relative;overflow:hidden; }
// .lp-price-card::before { content:'';position:absolute;top:0;left:10%;right:10%;height:1px;background:linear-gradient(90deg,transparent,var(--gold),transparent); }
// .lp-price-card-top { display:flex;align-items:flex-start;justify-content:space-between;margin-bottom:1.5rem; }
// .lp-price-badge { display:inline-block;background:linear-gradient(135deg,#D4AF37,#F0D060);color:#0A0A0A;border-radius:100px;padding:4px 14px;font-size:10px;font-weight:700;letter-spacing:1px;text-transform:uppercase; }
// .lp-price-amount { text-align:right; }
// .lp-price-num { font-family:var(--serif);font-size:4rem;font-weight:600;color:var(--text-primary);line-height:1;letter-spacing:-2px; }
// .lp-price-period { font-size:10px;font-weight:600;color:var(--text-muted2);letter-spacing:2px;text-transform:uppercase;margin-top:4px; }
// .lp-price-divider { height:0.5px;background:var(--border-color);margin:1.25rem 0; }
// .lp-price-list { list-style:none;display:flex;flex-direction:column;gap:0;margin-bottom:1.75rem; }
// .lp-price-item { display:flex;align-items:center;gap:10px;padding:0.55rem 0;font-size:13px;font-weight:500;color:var(--text-muted);border-bottom:0.5px solid var(--border-color); }
// .lp-price-item:last-child { border-bottom:none; }
// .lp-price-check { color:var(--gold);font-size:11px;flex-shrink:0; }

// /* ── FOOTER ── */
// .lp-footer { padding:3rem 3rem 4rem;border-top:0.5px solid var(--border-color);background:var(--surface); }
// .lp-footer-inner { max-width:1180px;margin:0 auto;display:flex;flex-direction:column;align-items:center;gap:1rem; }
// .lp-footer-logo { display:flex;align-items:center;gap:10px;font-family:var(--sans);font-size:15px;font-weight:600;color:var(--text-primary); }
// .lp-footer-logo em { color:var(--gold);font-style:normal;font-weight:700; }
// .lp-footer-tagline { font-size:13px;font-weight:400;color:var(--text-muted2); }
// .lp-footer-copy { font-size:12px;font-weight:400;color:var(--text-muted2);opacity:0.6; }

// /* ── THEME TOGGLE ── */
// .lp-theme-btn { width:40px;height:40px;border-radius:50%;border:0.5px solid var(--border-gold);background:var(--gold-subtle);cursor:pointer;display:flex;align-items:center;justify-content:center;transition:background 0.2s,transform 0.15s;flex-shrink:0; }
// .lp-theme-btn:hover { background:var(--gold-subtle2);transform:scale(1.08); }

// /* ── TABLET ── */
// @media (max-width: 900px) {
//   .lp-hero { grid-template-columns:1fr; text-align:center; padding:8rem 1.5rem 4rem; }
//   .lp-hero-sub { margin:0 auto 1rem; }
//   .lp-hero-ctas { justify-content:center; }
//   .lp-hero-stats { justify-content:center; }
//   .lp-hero-right { margin-top:3rem; }
//   .scan-scene { width:300px; height:380px; transform:scale(0.85); }
//   .lp-how-timeline { grid-template-columns:1fr; }
//   .lp-how-bridge { display:none; }
//   .lp-feat-bento { grid-template-columns:1fr 1fr; }
//   .lp-feat-bcard.hero { grid-column:span 2; }
//   .lp-feat-bcard.tall { grid-row:span 1; }
//   .lp-use-panel { grid-template-columns:1fr; }
//   .lp-pricing-cta-inner { grid-template-columns:1fr; }
//   .lp-nav { padding:1rem 1.5rem; }
//   .lp-nav.scrolled { padding:0.75rem 1.5rem; }
//   .lp-section { padding:4rem 1.5rem; }
//   .lp-pricing-cta { padding:4rem 1.5rem; }
//   .lp-hamburger { display:flex; }
//   .lp-nav-links { display:none; }
//   .lp-mobile-cluster { display:flex; }
//   .lp-trust { padding:1.25rem 1.5rem; }
// }
// /* ── MOBILE ── */
// @media (max-width: 600px) {
//   .lp-feat-bento { grid-template-columns:1fr; }
//   .lp-feat-bcard.hero { grid-column:span 1; }
//   .lp-hero-title { font-size:clamp(2.6rem,9vw,3.5rem); }
//   .lp-hero-stats { gap:1.5rem; }
//   .lp-stat-num { font-size:2rem; }
//   .lp-section { padding:3.5rem 1.25rem; }
//   .lp-section-title { font-size:clamp(2rem,7vw,2.8rem); }
//   .lp-use-panel { padding:1.75rem 1.25rem; }
//   .lp-pricing-cta { padding:3rem 1.25rem; }
//   .lp-pricing-headline { font-size:clamp(2.4rem,8vw,3rem); }
//   .lp-hero-ctas { flex-direction:column; align-items:center; }
//   .lp-hero-ctas .lp-btn-gold,
//   .lp-hero-ctas .lp-btn-ghost { width:100%; justify-content:center; }
//   .lp-trust-item { font-size:10px; }
//   .lp-trust-item-icon { width:30px; height:30px; font-size:1.1rem; }
// }
// `;

// /* ─────────────────────────────────────────
//    ENHANCEMENT 1 — COUNT-UP HOOK
// ───────────────────────────────────────── */
// function useCountUp(target, duration = 1800, suffix = "", started = false) {
//     const [display, setDisplay] = useState("0");
//     const [progress, setProgress] = useState(0);
//     const frameRef = useRef(null);

//     useEffect(() => {
//         if (!started) return;
//         // parse numeric target
//         const num = parseFloat(target.replace(/[^0-9.]/g, ""));
//         if (!num) { setDisplay(target); setProgress(100); return; }

//         const startTime = performance.now();
//         const animate = (now) => {
//             const elapsed = now - startTime;
//             const pct = Math.min(elapsed / duration, 1);
//             // easeOutExpo
//             const eased = pct === 1 ? 1 : 1 - Math.pow(2, -10 * pct);
//             const current = num * eased;
//             const prog = Math.round(pct * 100);

//             // format
//             let formatted;
//             if (Number.isInteger(num)) {
//                 formatted = Math.round(current).toString();
//             } else {
//                 formatted = current.toFixed(0);
//             }
//             setDisplay(formatted + suffix);
//             setProgress(prog);

//             if (pct < 1) {
//                 frameRef.current = requestAnimationFrame(animate);
//             }
//         };
//         frameRef.current = requestAnimationFrame(animate);
//         return () => cancelAnimationFrame(frameRef.current);
//     }, [started, target, duration, suffix]);

//     return { display, progress };
// }

// /* ─────────────────────────────────────────
//    STAT ITEM
// ───────────────────────────────────────── */
// function StatItem({ value, suffix, label, started, delay = 0 }) {
//     const [go, setGo] = useState(false);
//     useEffect(() => {
//         if (!started) return;
//         const t = setTimeout(() => setGo(true), delay);
//         return () => clearTimeout(t);
//     }, [started, delay]);

//     const { display, progress } = useCountUp(value, 1600, suffix, go);
//     const isCounting = go && progress < 100;

//     return (
//         <div className="lp-stat">
//             <div className={`lp-stat-num${isCounting ? " counting" : ""}`}>
//                 {value === "0" ? "0" : display}
//             </div>
//             <div className="lp-stat-label">{label}</div>
//             <div className="lp-stat-bar">
//                 <div className="lp-stat-bar-fill" style={{ width: `${progress}%` }} />
//             </div>
//         </div>
//     );
// }

// /* ─────────────────────────────────────────
//    QR / SCAN SCENE
// ───────────────────────────────────────── */
// const QR_PATTERN = [
//     [1, 1, 1, 0, 1, 0], [1, 0, 1, 1, 0, 1], [1, 1, 1, 0, 1, 1], [0, 1, 0, 1, 0, 0], [1, 0, 1, 1, 1, 0], [0, 1, 0, 0, 1, 1],
// ];

// function ScanScene() {
//     return (
//         <div className="scan-scene">
//             <div className="hero-orb-1" />

//             {/* Phone A — scanner */}
//             <div className="phone-scanner">
//                 <div className="phone-scanner-screen">
//                     <div className="scanner-viewfinder">
//                         <div className="vf-corner-tr" />
//                         <div className="vf-corner-bl" />
//                         <div className="scan-laser" />
//                         <div className="mini-qr">
//                             {QR_PATTERN.map((row, ri) =>
//                                 row.map((cell, ci) => (
//                                     <div key={`${ri}-${ci}`} className="mini-qr-cell"
//                                         style={{ background: cell ? "rgba(212,175,55,0.5)" : "transparent" }} />
//                                 ))
//                             )}
//                         </div>
//                         {/* Enhancement 5: flash overlay */}
//                         <div className="scanner-success-flash" />
//                     </div>
//                     <div className="scanner-label">Point at QR code</div>
//                 </div>
//             </div>

//             <div className="beam-line" />
//             <div className="beam-particle" />

//             {/* Enhancement 5: ripple rings */}
//             <div className="scan-ripple r1" />
//             <div className="scan-ripple r2" />
//             <div className="scan-ripple r3" />
//             {/* Enhancement 5: success badge */}
//             <div className="scan-success">
//                 <div className="scan-success-dot" />
//                 Contact Saved ✓
//             </div>

//             {/* Phone B */}
//             <div className="phone-profile">
//                 <div className="profile-card-wrap">
//                     <div className="profile-banner">
//                         <div className="profile-banner-img" />
//                         <div className="profile-banner-fade" />
//                         <div className="profile-avatar-ring">
//                             <div className="profile-avatar-inner">UN</div>
//                         </div>
//                     </div>
//                     <div className="profile-info">
//                         <div className="profile-info-name">User <em>Name</em></div>
//                         <div className="profile-info-role">Director · Company Name</div>
//                         <div className="profile-tiles">
//                             {[["📞", "Phone"], ["✉️", "Email"], ["📍", "Delhi"]].map(([, label]) => (
//                                 <div className="profile-tile" key={label}>
//                                     <div className="tile-dot"><div className="tile-dot-inner" /></div>
//                                     <div className="tile-text">{label}</div>
//                                 </div>
//                             ))}
//                         </div>
//                         <button className="profile-save-btn">↓ Save Contact</button>
//                         <div className="profile-tiles" style={{ marginTop: "1rem" }}>
//                             {[["🌐", "Website"], ["📄", "Resume"], ["ⓕ", "Facebook"]].map(([, label]) => (
//                                 <div className="profile-tile" key={label}>
//                                     <div className="tile-dot"><div className="tile-dot-inner" /></div>
//                                     <div className="tile-text">{label}</div>
//                                 </div>
//                             ))}
//                         </div>
//                     </div>
//                 </div>
//             </div>
//         </div>
//     );
// }

// /* ─────────────────────────────────────────
//    PROGRESS ARROW BRIDGE (Enhancement 2)
// ───────────────────────────────────────── */
// function ArrowBridge({ filled }) {
//     return (
//         <div className="lp-how-bridge">
//             <svg width="36" height="28" viewBox="0 0 36 28" fill="none" overflow="visible">
//                 {/* shaft */}
//                 <path
//                     d="M2 14 L26 14"
//                     className={`how-arrow-path${filled ? " filled" : ""}`}
//                     strokeWidth="1.5"
//                     strokeLinecap="round"
//                     fill="none"
//                     style={{ strokeDasharray: 28, strokeDashoffset: filled ? 0 : 28, transition: "stroke-dashoffset 0.8s cubic-bezier(0.22,1,0.36,1), stroke 0.4s ease" }}
//                 />
//                 {/* head */}
//                 <path
//                     d="M20 7 L28 14 L20 21"
//                     className={`how-arrow-path${filled ? " filled" : ""}`}
//                     strokeWidth="1.5"
//                     strokeLinecap="round"
//                     strokeLinejoin="round"
//                     fill="none"
//                     style={{ strokeDasharray: 24, strokeDashoffset: filled ? 0 : 24, transition: "stroke-dashoffset 0.6s cubic-bezier(0.22,1,0.36,1) 0.3s, stroke 0.4s ease 0.3s" }}
//                 />
//             </svg>
//         </div>
//     );
// }

// /* ─────────────────────────────────────────
//    USE CASES
// ───────────────────────────────────────── */
// const USE_CASES = [
//     { icon: "👔", role: "Job Seekers", tagline: "Land your next opportunity faster", desc: "Walk into every interview with a digital card that includes your resume, LinkedIn, GitHub, and portfolio — all in one elegant scan.", highlights: ["Resume attached & downloadable", "LinkedIn & GitHub links", "QR code for instant sharing", "Save contact in one tap"] },
//     { icon: "💼", role: "Freelancers", tagline: "Win clients before you even speak", desc: "Share everything a client needs — portfolio, social proof, and contact — all in a single professional link. Close deals faster.", highlights: ["Portfolio & website link", "WhatsApp quick contact", "Instagram & social proof", "Professional digital presence"] },
//     { icon: "🏢", role: "Business Owners", tagline: "Network smarter, not harder", desc: "Stop spending on printed cards that get lost. Share via QR at events — always updated, never outdated, never thrown away.", highlights: ["Always up-to-date details", "Google Wallet for offline use", "QR code for events & meetups", "Share via WhatsApp or email"] },
//     { icon: "🎓", role: "Students", tagline: "Build your professional identity early", desc: "Stand out in campus placements and internship drives. Show your skills, projects, and social profiles before you say a word.", highlights: ["GitHub & project links", "Resume upload for placements", "LinkedIn profile showcase", "Free forever — no cost"] },
//     { icon: "📣", role: "Sales Pros", tagline: "Close the follow-up gap instantly", desc: "At trade shows or cold visits — one QR scan and your prospect has everything. Number, email, website, product links in one place.", highlights: ["Instant QR share at events", "Website & product links", "Save contact in one tap", "Google Wallet for quick access"] },
//     { icon: "🧑‍💻", role: "Developers", tagline: "Your entire dev profile in one link", desc: "GitHub, LinkedIn, Twitter, personal site, resume — all in one clean card. Share at hackathons or in open source communities.", highlights: ["GitHub & portfolio links", "Twitter / X profile", "Resume for job applications", "Professional dev identity"] },
// ];

// function UseCasesSection({ navigate }) {
//     const [active, setActive] = useState(0);
//     const [tabKey, setTabKey] = useState(0);
//     const cur = USE_CASES[active];

//     const switchTab = (i) => {
//         setActive(i);
//         setTabKey(k => k + 1);
//     };

//     return (
//         <section className="lp-section">
//             <div className="lp-section-inner">
//                 <span className="lp-eyebrow reveal from-up">Who is it for</span>
//                 <h2 className="lp-section-title reveal from-up d1">Built for every <em>professional</em></h2>
//                 <p className="lp-section-sub reveal from-up d2">Whether you're a student or a CEO — your digital card works for you.</p>

//                 <div className="lp-use-tabs reveal from-up d3">
//                     {USE_CASES.map((u, i) => (
//                         <button key={i} className="lp-use-tab" onClick={() => switchTab(i)}
//                             style={{
//                                 border: active === i ? "0.5px solid var(--border-gold)" : "0.5px solid var(--border-color)",
//                                 background: active === i ? "var(--gold-subtle2)" : "transparent",
//                                 color: active === i ? "var(--gold)" : "var(--text-muted)",
//                             }}>
//                             {u.icon} {u.role}
//                         </button>
//                     ))}
//                 </div>

//                 <div className="lp-use-panel reveal from-up d4" style={{ borderColor: "var(--border-gold)" }}>
//                     {/* Left content — re-animates on tab switch */}
//                     <div key={`left-${tabKey}`} className="lp-use-content">
//                         <div className="lp-use-badge"><span>{cur.icon}</span><span style={{ fontSize: "12px", letterSpacing: "0.5px" }}>{cur.role}</span></div>
//                         <div className="lp-use-tagline">{cur.tagline}</div>
//                         <p className="lp-use-desc">{cur.desc}</p>
//                         <button className="lp-btn-gold" onClick={() => navigate("/create")}>Create My Card — Free →</button>
//                     </div>

//                     {/* Right highlights — each item slides in from right */}
//                     <div key={`right-${tabKey}`} className="lp-use-highlights">
//                         {cur.highlights.map((h, i) => (
//                             <div className="lp-use-hl" key={i} style={{ animationDelay: `${0.05 + i * 0.07}s` }}>
//                                 <div className="lp-use-hl-check">✓</div>
//                                 <span className="lp-use-hl-text">{h}</span>
//                             </div>
//                         ))}
//                     </div>
//                 </div>
//             </div>
//         </section>
//     );
// }

// /* ─────────────────────────────────────────
//    MAIN COMPONENT
// ───────────────────────────────────────── */
// export default function LandingPage() {
//     const navigate = useNavigate();
//     const [scrolled, setScrolled] = useState(false);
//     const [isLight, setIsLight] = useState(false);
//     const [menuOpen, setMenuOpen] = useState(false);

//     // close menu on resize to desktop
//     useEffect(() => {
//         const onResize = () => { if (window.innerWidth > 900) setMenuOpen(false); };
//         window.addEventListener("resize", onResize);
//         return () => window.removeEventListener("resize", onResize);
//     }, []);
//     const [statsStarted, setStatsStarted] = useState(false);
//     // Enhancement 2: track if how-section is in view for arrow progress
//     const [howVisible, setHowVisible] = useState(false);
//     // Enhancement 4: track when social card enters view
//     const socialCardRef = useRef(null);

//     const statsRef = useRef(null);
//     const howRef = useRef(null);

//     /* ── scroll nav ── */
//     useEffect(() => {
//         const onScroll = () => setScrolled(window.scrollY > 40);
//         window.addEventListener("scroll", onScroll);
//         return () => window.removeEventListener("scroll", onScroll);
//     }, []);

//     /* ── unified IntersectionObserver ── */
//     useEffect(() => {
//         const observers = [];

//         // 1. directional reveals
//         const revealEls = document.querySelectorAll(".reveal");
//         const revealObs = new IntersectionObserver(
//             (entries) => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add("visible"); }),
//             { threshold: 0.1 }
//         );
//         revealEls.forEach(el => revealObs.observe(el));
//         observers.push(revealObs);

//         // 2. count-up trigger
//         if (statsRef.current) {
//             const statsObs = new IntersectionObserver(
//                 ([entry]) => { if (entry.isIntersecting) setStatsStarted(true); },
//                 { threshold: 0.5 }
//             );
//             statsObs.observe(statsRef.current);
//             observers.push(statsObs);
//         }

//         // 3. how-section for arrow progress connector
//         if (howRef.current) {
//             const howObs = new IntersectionObserver(
//                 ([entry]) => { if (entry.isIntersecting) setHowVisible(true); },
//                 { threshold: 0.3 }
//             );
//             howObs.observe(howRef.current);
//             observers.push(howObs);
//         }

//         // 4. social pills card
//         const pillCards = document.querySelectorAll(".lp-feat-bcard.has-pills");
//         const pillObs = new IntersectionObserver(
//             (entries) => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add("pills-visible"); }),
//             { threshold: 0.4 }
//         );
//         pillCards.forEach(c => pillObs.observe(c));
//         observers.push(pillObs);

//         return () => observers.forEach(o => o.disconnect());
//     }, []);

//     return (
//         <div className={isLight ? "lp-light" : ""} style={{ background: isLight ? "#FFFFFF" : "#080808", minHeight: "100vh" }}>
//             <style dangerouslySetInnerHTML={{ __html: GLOBAL_CSS }} />

//             {/* ── NAV ── */}
//             {/* ── MOBILE DRAWER ── */}
//             <div className={`lp-mobile-menu${menuOpen ? " open" : ""}`}>
//                 <div className="lp-mobile-menu-backdrop" onClick={() => setMenuOpen(false)} />
//                 <div className="lp-mobile-menu-panel">
//                     <div className="lp-mobile-menu-header">
//                         <a href="/" className="lp-logo" onClick={() => setMenuOpen(false)}>
//                             <svg width="28" height="28" viewBox="0 0 34 34" fill="none">
//                                 <rect width="34" height="34" rx="9" fill={isLight ? "#F0EDE6" : "#1A1A1A"} />
//                                 <rect width="34" height="34" rx="9" stroke="var(--border-gold)" strokeWidth="0.5" />
//                                 <circle cx="15" cy="13" r="5" stroke="var(--gold)" strokeWidth="1.5" fill="none" />
//                                 <path d="M7 27 C7 21 10 18 15 18 C20 18 23 21 23 27" stroke="var(--gold)" strokeWidth="1.5" strokeLinecap="round" fill="none" />
//                                 <circle cx="27" cy="23" r="1.8" fill="var(--gold)" />
//                             </svg>
//                             myprofile<em>corner</em>
//                         </a>
//                         <button className="lp-mobile-close" onClick={() => setMenuOpen(false)}>✕</button>
//                     </div>

//                     <div className="lp-mobile-links">
//                         <a href="#how" className="lp-mobile-link" onClick={() => setMenuOpen(false)}><span className="lp-mobile-link-icon">⚡</span>How it works</a>
//                         <a href="#features" className="lp-mobile-link" onClick={() => setMenuOpen(false)}><span className="lp-mobile-link-icon">✦</span>Features</a>
//                         <a href="#pricing" className="lp-mobile-link" onClick={() => setMenuOpen(false)}><span className="lp-mobile-link-icon">₹</span>Pricing</a>
//                     </div>

//                     <div className="lp-mobile-divider" />

//                     <div className="lp-mobile-menu-footer">
//                         <button className="lp-mobile-cta" onClick={() => { setMenuOpen(false); navigate("/create"); }}>
//                             Create My Card — Free →
//                         </button>
//                         <div className="lp-mobile-tagline">100% Free · No app required · 2 min setup</div>
//                     </div>
//                 </div>
//             </div>

//             <nav className={`lp-nav${scrolled ? " scrolled" : ""}`}>
//                 <div className="lp-nav-inner">
//                     <a href="/" className="lp-logo">
//                         <svg className="lp-logo-icon" viewBox="0 0 34 34" fill="none">
//                             <rect width="34" height="34" rx="9" fill={isLight ? "#F0EDE6" : "#1A1A1A"} />
//                             <rect width="34" height="34" rx="9" stroke="var(--border-gold)" strokeWidth="0.5" />
//                             <circle cx="15" cy="13" r="5" stroke="var(--gold)" strokeWidth="1.5" fill="none" />
//                             <path d="M7 27 C7 21 10 18 15 18 C20 18 23 21 23 27" stroke="var(--gold)" strokeWidth="1.5" strokeLinecap="round" fill="none" />
//                             <path d="M23 11 L27 11 L27 23" stroke="var(--gold)" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" fill="none" opacity="0.6" />
//                             <circle cx="27" cy="23" r="1.8" fill="var(--gold)" />
//                         </svg>
//                         myprofile<em>corner</em>
//                     </a>

//                     {/* Desktop links */}
//                     <div className="lp-nav-links">
//                         <a href="#how" className="lp-nav-link">How it works</a>
//                         <a href="#features" className="lp-nav-link">Features</a>
//                         <a href="#pricing" className="lp-nav-link">Pricing</a>
//                         <button className="lp-theme-btn" onClick={() => setIsLight(p => !p)} aria-label="Toggle theme">
//                             {isLight ? (
//                                 <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--gold)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
//                                     <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
//                                 </svg>
//                             ) : (
//                                 <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--gold)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
//                                     <circle cx="12" cy="12" r="5" /><line x1="12" y1="1" x2="12" y2="3" /><line x1="12" y1="21" x2="12" y2="23" />
//                                     <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" /><line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
//                                     <line x1="1" y1="12" x2="3" y2="12" /><line x1="21" y1="12" x2="23" y2="12" />
//                                     <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" /><line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
//                                 </svg>
//                             )}
//                         </button>
//                         <button className="lp-nav-cta" onClick={() => navigate("/create")}>Create Free Card</button>
//                     </div>

//                     {/* Mobile right cluster: theme toggle + hamburger */}
//                     <div className="lp-mobile-cluster">
//                         <button className="lp-theme-btn lp-theme-btn-mobile" onClick={() => setIsLight(p => !p)} aria-label="Toggle theme">
//                             {isLight ? (
//                                 <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--gold)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
//                                     <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
//                                 </svg>
//                             ) : (
//                                 <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--gold)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
//                                     <circle cx="12" cy="12" r="5" /><line x1="12" y1="1" x2="12" y2="3" /><line x1="12" y1="21" x2="12" y2="23" />
//                                     <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" /><line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
//                                     <line x1="1" y1="12" x2="3" y2="12" /><line x1="21" y1="12" x2="23" y2="12" />
//                                     <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" /><line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
//                                 </svg>
//                             )}
//                         </button>
//                         <button
//                             className={`lp-hamburger${menuOpen ? " open" : ""}`}
//                             onClick={() => setMenuOpen(p => !p)}
//                             aria-label="Open menu"
//                         >
//                             <span /><span /><span />
//                         </button>
//                     </div>

//                 </div>
//             </nav>

//             {/* ── HERO ── */}
//             <section style={{ background: isLight ? "#FFFFFF" : "#080808", overflow: "hidden", position: "relative" }}>
//                 <div className="lp-hero">
//                     <div style={{ position: "absolute", width: "600px", height: "600px", borderRadius: "50%", background: "radial-gradient(circle, var(--gold-subtle) 0%, transparent 65%)", top: "-200px", left: "-100px", pointerEvents: "none" }} />

//                     <div className="lp-hero-left">
//                         <div className="lp-hero-eyebrow reveal from-down">
//                             <div className="lp-hero-eyebrow-dot" /> 100% Free · No App Needed
//                         </div>
//                         <h1 className="lp-hero-title reveal from-left">
//                             Your Digital<br />Identity, <em>Always</em><br />in Your Pocket
//                         </h1>
//                         <p className="lp-hero-sub reveal from-left d1">
//                             Replace paper visiting cards with a smart digital card. Share your profile instantly via QR code, link, or Google Wallet — no printing, no waste.
//                         </p>

//                         {/* Enhancement 1: Count-up stats */}
//                         <div className="lp-hero-stats" ref={statsRef}>
//                             <StatItem value="100" suffix="%" label="Free forever" started={statsStarted} delay={0} />
//                             <StatItem value="2" suffix=" min" label="Setup time" started={statsStarted} delay={200} />
//                             <StatItem value="0" suffix="" label="Paper waste" started={statsStarted} delay={400} />
//                         </div>

//                         <div className="lp-hero-ctas reveal from-up d2">
//                             <button className="lp-btn-gold" onClick={() => navigate("/create")}>Create My Card — It's Free →</button>
//                             <a href="#how" className="lp-btn-ghost">See how it works</a>
//                         </div>
//                     </div>

//                     <div className="lp-hero-right reveal from-right">
//                         <ScanScene />
//                     </div>
//                 </div>
//             </section>

//             <div className="gold-rule" />

//             {/* ── TRUST BAR ── */}
//             <div className="lp-trust">
//                 <span className="lp-trust-label">Works seamlessly with</span>
//                 <div className="lp-trust-items">
//                     {[["🔥", "Firebase"], ["🪙", "Google Wallet"], ["📱", "Android & iOS"], ["🔗", "Any Browser"]].map(([icon, label]) => (
//                         <div className="lp-trust-item" key={label}>
//                             <div className="lp-trust-item-icon">{icon}</div>
//                             <span>{label}</span>
//                         </div>
//                     ))}
//                 </div>
//             </div>

//             <div className="gold-rule" />

//             {/* ── HOW IT WORKS ── */}
//             <section id="how" className="lp-section" ref={howRef}>
//                 <div className="lp-section-inner">
//                     <span className="lp-eyebrow reveal from-up">How it works</span>
//                     <h2 className="lp-section-title reveal from-up d1">From setup to sharing <em>in 3 steps</em></h2>
//                     <p className="lp-section-sub reveal from-up d2">No app downloads. No tech skills required. Just fill, share, impress.</p>

//                     {/* Enhancement 2: progress connectors + directional card reveals */}
//                     <div className="lp-how-timeline">
//                         <div className="lp-how-card reveal from-left d1">
//                             <div className="lp-how-num">01</div>
//                             <div className="lp-how-icon-ring">✏️</div>
//                             <div className="lp-how-step-label">Step 01</div>
//                             <div className="lp-how-title">Fill Your Profile</div>
//                             <p className="lp-how-desc">Add your name, photo, contact info, social links, resume & website. Takes less than 2 minutes.</p>
//                         </div>

//                         <ArrowBridge filled={howVisible} />

//                         <div className="lp-how-card reveal from-up d3">
//                             <div className="lp-how-num">02</div>
//                             <div className="lp-how-icon-ring">🎴</div>
//                             <div className="lp-how-step-label">Step 02</div>
//                             <div className="lp-how-title">Get Your Digital Card</div>
//                             <p className="lp-how-desc">Instantly get a beautiful card with a unique QR code & shareable link — no design skills needed.</p>
//                         </div>

//                         <ArrowBridge filled={howVisible} />

//                         <div className="lp-how-card reveal from-right d5">
//                             <div className="lp-how-num">03</div>
//                             <div className="lp-how-icon-ring">🚀</div>
//                             <div className="lp-how-step-label">Step 03</div>
//                             <div className="lp-how-title">Share Anywhere</div>
//                             <p className="lp-how-desc">Show QR in meetings, add to Google Wallet, or send your link via WhatsApp, email, or LinkedIn.</p>
//                         </div>
//                     </div>
//                 </div>
//             </section>

//             <div className="gold-rule" />

//             {/* ── FEATURES ── */}
//             <section id="features" className="lp-section">
//                 <div className="lp-section-inner">
//                     <span className="lp-eyebrow reveal from-up">Features</span>
//                     <h2 className="lp-section-title reveal from-up d1">Everything a modern <em>professional</em> needs</h2>
//                     <p className="lp-section-sub reveal from-up d2">One card. Every detail. Always up-to-date.</p>

//                     {/* Enhancement 4: bento with shimmer + social pill stagger */}
//                     <div className="lp-feat-bento">

//                         {/* Hero: Google Wallet — reveals from left */}
//                         <div className="lp-feat-bcard hero reveal from-left d1">
//                             <div className="lp-feat-bicon">🪙</div>
//                             <div className="lp-feat-btag">Premium Feature</div>
//                             <div className="lp-feat-btitle">Google Wallet Pass</div>
//                             <p className="lp-feat-bdesc">Add your card to Google Wallet and access it offline — at meetings, events, or anywhere without internet.</p>
//                             <div className="lp-feat-wallet">
//                                 <div className="lp-wallet-chip">G Pay</div>
//                                 <div className="lp-wallet-chip">Wallet</div>
//                                 <div className="lp-wallet-chip">Offline ✓</div>
//                             </div>
//                         </div>

//                         {/* QR tall — reveals from right */}
//                         <div className="lp-feat-bcard tall reveal from-right d2">
//                             <div className="lp-feat-bicon">📲</div>
//                             <div className="lp-feat-btag">Instant Share</div>
//                             <div className="lp-feat-btitle">Unique QR Code</div>
//                             <p className="lp-feat-bdesc">Every card gets a dedicated QR. One scan opens your full profile — no typing, no friction.</p>
//                             <div className="lp-qr-art">
//                                 {[1, 1, 1, 0, 1, 1, 0, 1, 0, 0, 1, 1, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 1, 1].map((v, i) => (
//                                     <div key={i} className="lp-qr-cell" style={{ background: v ? "var(--gold)" : "var(--gold-subtle)", borderRadius: "2px", aspectRatio: "1" }} />
//                                 ))}
//                             </div>
//                         </div>

//                         {/* Photo — from below */}
//                         <div className="lp-feat-bcard reveal from-up d3">
//                             <div className="lp-feat-bicon">📸</div>
//                             <div className="lp-feat-btag">Identity</div>
//                             <div className="lp-feat-btitle">Profile Photo</div>
//                             <p className="lp-feat-bdesc">Your face on your card — and inside Google Wallet too.</p>
//                         </div>

//                         {/* Save contact — from below */}
//                         <div className="lp-feat-bcard reveal from-up d4">
//                             <div className="lp-feat-bicon">💾</div>
//                             <div className="lp-feat-btag">One Tap</div>
//                             <div className="lp-feat-btitle">Save Contact</div>
//                             <p className="lp-feat-bdesc">Visitors save you directly to their phone contacts with a single tap.</p>
//                         </div>

//                         {/* Hero: Social Links — from left, pills stagger */}
//                         <div className="lp-feat-bcard hero has-pills reveal from-left d2">
//                             <div className="lp-feat-bicon">🔗</div>
//                             <div className="lp-feat-btag">Connections</div>
//                             <div className="lp-feat-btitle">All Social Links</div>
//                             <p className="lp-feat-bdesc">LinkedIn, GitHub, Instagram, Twitter, WhatsApp, YouTube — everything that matters in one place.</p>
//                             <div className="lp-feat-socials">
//                                 {["in LinkedIn", "⌥ GitHub", "✉ WhatsApp", "▶ YouTube", "📷 Insta"].map(s => (
//                                     <span key={s} className="lp-social-pill">{s}</span>
//                                 ))}
//                             </div>
//                         </div>

//                         <div className="lp-feat-bcard reveal from-up d3">
//                             <div className="lp-feat-bicon">📄</div>
//                             <div className="lp-feat-btag">Career</div>
//                             <div className="lp-feat-btitle">Resume Upload</div>
//                             <p className="lp-feat-bdesc">Attach your PDF resume. Recruiters can download it instantly.</p>
//                         </div>
//                         <div className="lp-feat-bcard reveal from-up d4">
//                             <div className="lp-feat-bicon">✏️</div>
//                             <div className="lp-feat-btag">Always Fresh</div>
//                             <div className="lp-feat-btitle">Edit Anytime</div>
//                             <p className="lp-feat-bdesc">Changed jobs? New number? Update instantly — same link forever.</p>
//                         </div>
//                         <div className="lp-feat-bcard reveal from-right d5">
//                             <div className="lp-feat-bicon">🌐</div>
//                             <div className="lp-feat-btag">Universal</div>
//                             <div className="lp-feat-btitle">Works Everywhere</div>
//                             <p className="lp-feat-bdesc">Opens in any browser on any device. No app install ever required.</p>
//                         </div>

//                     </div>
//                 </div>
//             </section>

//             <div className="gold-rule" />

//             {/* ── USE CASES ── */}
//             <UseCasesSection navigate={navigate} />

//             <div className="gold-rule" />

//             {/* ── PRICING + CTA MERGED ── */}
//             <section id="pricing" className="lp-pricing-cta">
//                 <div className="lp-pricing-cta-inner">
//                     <div className="lp-pricing-copy">
//                         <span className="lp-pricing-eyebrow reveal from-left">Pricing</span>
//                         <h2 className="lp-pricing-headline reveal from-left d1">Stop carrying<br /><em>paper cards.</em></h2>
//                         <p className="lp-pricing-sub reveal from-left d2">Create your digital card in under 2 minutes. No hidden charges. No credit card. Free forever.</p>
//                         <div className="lp-pricing-perks reveal from-left d3">
//                             {["No app download needed", "Unique QR code & shareable link", "Google Wallet pass included", "Edit anytime — same link forever", "Works on every device & browser"].map(p => (
//                                 <div className="lp-pricing-perk" key={p}>
//                                     <div className="lp-perk-tick">✓</div><span>{p}</span>
//                                 </div>
//                             ))}
//                         </div>
//                         <button className="lp-btn-gold reveal from-up d4" onClick={() => navigate("/create")}>Create My Card Now →</button>
//                     </div>
//                     <div className="lp-price-card reveal from-right d2">
//                         <div className="lp-price-card-top">
//                             <div className="lp-price-badge">Most Popular</div>
//                             <div className="lp-price-amount">
//                                 <div className="lp-price-num">₹0</div>
//                                 <div className="lp-price-period">Forever Free</div>
//                             </div>
//                         </div>
//                         <div className="lp-price-divider" />
//                         <ul className="lp-price-list">
//                             {["1 Digital Visiting Card", "Unique QR Code & Link", "Profile Photo Upload", "Resume Upload (PDF)", "All Social Media Links", "Google Wallet Pass", "Save Contact (.vcf)", "Edit Anytime", "No App Required"].map(item => (
//                                 <li className="lp-price-item" key={item}><span className="lp-price-check">✦</span>{item}</li>
//                             ))}
//                         </ul>
//                         <button className="lp-btn-gold" style={{ width: "100%", justifyContent: "center" }} onClick={() => navigate("/create")}>Get Started Free →</button>
//                     </div>
//                 </div>
//             </section>

//             <div className="gold-rule" />

//             {/* ── FOOTER ── */}
//             <footer className="lp-footer">
//                 <div className="lp-footer-inner">
//                     <div className="lp-footer-logo">
//                         <svg width="28" height="28" viewBox="0 0 34 34" fill="none">
//                             <rect width="34" height="34" rx="9" fill={isLight ? "#F0EDE6" : "#1A1A1A"} />
//                             <rect width="34" height="34" rx="9" stroke="var(--border-gold)" strokeWidth="0.5" />
//                             <circle cx="15" cy="13" r="5" stroke="var(--gold)" strokeWidth="1.5" fill="none" />
//                             <path d="M7 27 C7 21 10 18 15 18 C20 18 23 21 23 27" stroke="var(--gold)" strokeWidth="1.5" strokeLinecap="round" fill="none" />
//                             <path d="M23 11 L27 11 L27 23" stroke="var(--gold)" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" fill="none" opacity="0.6" />
//                             <circle cx="27" cy="23" r="1.8" fill="var(--gold)" />
//                         </svg>
//                         myprofile<em>corner</em>
//                     </div>
//                     <p className="lp-footer-tagline">Your digital identity, always in your pocket.</p>
//                     <p className="lp-footer-copy">© {new Date().getFullYear()} MyProfileCorner · Made with ♥ in India</p>
//                 </div>
//             </footer>
//         </div>
//     );
// }


// // import React, { useState, useEffect } from "react";
// // import { useNavigate } from "react-router-dom";

// // /* ─────────────────────────────────────────
// //    AESTHETIC: "Dark Editorial Tech"
// //    Fonts: Syne (bold display) + Plus Jakarta Sans (warm body) + Space Mono (data)
// //    Colors: Indigo #6C63FF · Teal #00D4AA · Pink #FF6B9D
// // ───────────────────────────────────────── */

// // const FONT_LINK = `@import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;500;600;700;800&family=Plus+Jakarta+Sans:ital,wght@0,300;0,400;0,500;0,600;0,700;1,400&family=Space+Mono:wght@400;700&display=swap');`;

// // const GLOBAL_CSS = `
// // ${FONT_LINK}

// // *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

// // :root {
// //   --bg:   #070810;
// //   --s1:   #0E0F1C;
// //   --s2:   #13152A;
// //   --s3:   #181A30;
// //   --a1:   #6C63FF;
// //   --a2:   #00D4AA;
// //   --a3:   #FF6B9D;
// //   --bd:   rgba(255,255,255,0.07);
// //   --bd2:  rgba(255,255,255,0.12);
// //   --w:    #F0F0FF;
// //   --m:    rgba(240,240,255,0.52);
// //   --m2:   rgba(240,240,255,0.3);
// //   --grad: linear-gradient(135deg,#6C63FF,#00D4AA,#FF6B9D);
// //   --grad2: linear-gradient(135deg,#6C63FF,#00D4AA);
// //   --D: 'Syne', sans-serif;
// //   --B: 'Plus Jakarta Sans', sans-serif;
// //   --M: 'Space Mono', monospace;
// // }

// // html { scroll-behavior: smooth; }
// // body {
// //   background: var(--bg);
// //   color: var(--w);
// //   font-family: var(--B);
// //   overflow-x: hidden;
// //   min-height: 100vh;
// // }

// // body::before {
// //   content: '';
// //   position: fixed; inset: 0; z-index: 0; pointer-events: none;
// //   background-image:
// //     linear-gradient(rgba(108,99,255,0.04) 1px, transparent 1px),
// //     linear-gradient(90deg, rgba(108,99,255,0.04) 1px, transparent 1px);
// //   background-size: 60px 60px;
// // }

// // .gt {
// //   background: var(--grad2);
// //   -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text;
// // }
// // .gt3 {
// //   background: var(--grad);
// //   -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text;
// // }

// // .blob {
// //   position: fixed; border-radius: 50%;
// //   filter: blur(100px); pointer-events: none; z-index: 0;
// // }
// // .b1 { width:600px;height:600px; background:rgba(108,99,255,0.15); top:-200px;left:-150px; }
// // .b2 { width:400px;height:400px; background:rgba(0,212,170,0.10); top:200px;right:-100px; }
// // .b3 { width:350px;height:350px; background:rgba(255,107,157,0.08); bottom:100px;left:35%; }

// // /* NAV */
// // .lp-nav {
// //   position: fixed; top: 0; left: 0; right: 0; z-index: 200;
// //   padding: 16px 40px;
// //   transition: all 0.35s;
// // }
// // .lp-nav.scrolled {
// //   background: rgba(7,8,16,0.85);
// //   backdrop-filter: blur(24px) saturate(180%);
// //   border-bottom: 0.5px solid var(--bd2);
// //   padding: 12px 40px;
// // }
// // .lp-nav-inner {
// //   max-width: 1140px; margin: 0 auto;
// //   display: flex; align-items: center; justify-content: space-between;
// // }
// // .lp-logo { display: flex; align-items: center; gap: 10px; text-decoration: none; }
// // .lp-logo-mark {
// //   width: 36px; height: 36px; border-radius: 10px;
// //   background: var(--grad2);
// //   display: flex; align-items: center; justify-content: center;
// //   font-family: var(--D); font-size: 17px; font-weight: 800; color: #070810;
// //   flex-shrink: 0;
// // }
// // .lp-logo-text {
// //   font-family: var(--D); font-size: 15px; font-weight: 600; color: var(--w);
// //   letter-spacing: -0.3px;
// // }
// // .lp-logo-text strong { font-weight: 800; color: var(--a2); }
// // .lp-nav-links { display: flex; align-items: center; gap: 32px; }
// // .lp-nav-link {
// //   font-size: 14px; font-weight: 500; color: var(--m);
// //   text-decoration: none; transition: color 0.2s;
// // }
// // .lp-nav-link:hover { color: var(--w); }
// // .lp-theme-btn {
// //   width: 38px; height: 38px; border-radius: 50%;
// //   border: 0.5px solid var(--bd2); background: var(--s1);
// //   cursor: pointer; display: flex; align-items: center; justify-content: center;
// //   transition: border-color 0.2s, background 0.2s; flex-shrink: 0;
// // }
// // .lp-theme-btn:hover { border-color: var(--a1); background: rgba(108,99,255,0.1); }
// // .lp-nav-cta {
// //   padding: 10px 22px; border: none; border-radius: 8px;
// //   background: var(--grad2);
// //   color: #070810; font-family: var(--B); font-size: 13px; font-weight: 700;
// //   cursor: pointer; transition: opacity 0.2s, transform 0.15s;
// //   position: relative; overflow: hidden;
// // }
// // .lp-nav-cta::after { content:''; position:absolute; inset:0; background:rgba(255,255,255,0.15); opacity:0; transition:opacity .2s; }
// // .lp-nav-cta:hover { opacity: 0.9; transform: translateY(-1px); }
// // .lp-nav-cta:hover::after { opacity: 1; }

// // /* HERO */
// // .lp-hero-wrap { position: relative; z-index: 1; overflow: hidden; }
// // .lp-hero {
// //   position: relative; z-index: 1;
// //   max-width: 1140px; margin: 0 auto;
// //   padding: 130px 40px 90px;
// //   display: grid; grid-template-columns: 1fr 420px;
// //   gap: 60px; align-items: center;
// // }
// // .lp-pill {
// //   display: inline-flex; align-items: center; gap: 8px;
// //   padding: 5px 16px 5px 6px; border-radius: 100px;
// //   border: 0.5px solid rgba(0,212,170,0.3);
// //   background: rgba(0,212,170,0.06);
// //   margin-bottom: 28px;
// // }
// // .lp-pill-dot {
// //   width: 22px; height: 22px; border-radius: 50%; background: var(--grad2);
// //   display: flex; align-items: center; justify-content: center;
// // }
// // .lp-pill-dot::after { content:''; width:7px;height:7px;border-radius:50%;background:#070810; }
// // .lp-pill-text { font-family: var(--M); font-size: 11px; color: var(--a2); letter-spacing: 0.5px; }
// // .lp-h1 {
// //   font-family: var(--D);
// //   font-size: clamp(3.2rem, 5.8vw, 5.4rem);
// //   font-weight: 800; line-height: 1.0; letter-spacing: -2px;
// //   color: var(--w); margin-bottom: 24px;
// // }
// // .lp-h1 .line-grad {
// //   display: block; background: var(--grad);
// //   -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text;
// // }
// // .lp-hero-sub {
// //   font-size: 16px; font-weight: 400; line-height: 1.85;
// //   color: var(--m); max-width: 460px; margin-bottom: 36px;
// // }
// // .lp-ctas { display: flex; gap: 12px; flex-wrap: wrap; margin-bottom: 48px; }
// // .lp-btn-primary {
// //   display: inline-flex; align-items: center; gap: 8px;
// //   padding: 14px 28px; border: none; border-radius: 10px;
// //   background: var(--grad2);
// //   color: #070810; font-family: var(--B); font-size: 14px; font-weight: 700;
// //   cursor: pointer; position: relative; overflow: hidden;
// //   transition: transform 0.2s, opacity 0.2s;
// // }
// // .lp-btn-primary::after { content:''; position:absolute;inset:0;background:rgba(255,255,255,0.18);opacity:0;transition:opacity .2s; }
// // .lp-btn-primary:hover { transform: translateY(-2px); }
// // .lp-btn-primary:hover::after { opacity: 1; }
// // .lp-btn-ghost {
// //   display: inline-flex; align-items: center; gap: 8px;
// //   padding: 14px 22px; border: 0.5px solid var(--bd2); border-radius: 10px;
// //   background: transparent; color: var(--m);
// //   font-family: var(--B); font-size: 14px; font-weight: 500;
// //   cursor: pointer; text-decoration: none;
// //   transition: border-color 0.2s, color 0.2s;
// // }
// // .lp-btn-ghost:hover { border-color: rgba(108,99,255,0.5); color: var(--w); }
// // .lp-stats {
// //   display: flex; gap: 36px;
// //   padding-top: 32px; border-top: 0.5px solid var(--bd);
// // }
// // .lp-stat-n {
// //   font-family: var(--M); font-size: 28px; font-weight: 700; line-height: 1;
// //   background: var(--grad2);
// //   -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text;
// // }
// // .lp-stat-l { font-size: 12px; color: var(--m2); margin-top: 4px; }

// // /* SCENE */
// // .scene { position: relative; width: 380px; height: 460px; flex-shrink: 0; }
// // .phone-a {
// //   position: absolute; left: 0; top: 60px;
// //   width: 155px; height: 270px;
// //   background: var(--s2); border-radius: 22px;
// //   border: 0.5px solid rgba(255,255,255,0.09);
// //   overflow: hidden; z-index: 3;
// //   box-shadow: 0 20px 60px rgba(0,0,0,0.7);
// //   animation: pa 0.9s cubic-bezier(.34,1.56,.64,1) both 0.3s;
// //   transform: rotate(-6deg);
// // }
// // @keyframes pa { from{opacity:0;transform:translateX(-28px) rotate(-6deg)} to{opacity:1;transform:rotate(-6deg)} }
// // .phone-b {
// //   position: absolute; right: 0; top: 0;
// //   width: 176px; height: 314px;
// //   background: #0D0E1A; border-radius: 24px;
// //   border: 0.5px solid rgba(108,99,255,0.3);
// //   overflow: hidden; z-index: 4;
// //   box-shadow: 0 30px 80px rgba(0,0,0,0.75), 0 0 60px rgba(108,99,255,0.1);
// //   animation: pb 0.9s cubic-bezier(.34,1.56,.64,1) both 0.5s;
// //   transform: rotate(4deg);
// // }
// // @keyframes pb { from{opacity:0;transform:translateY(28px) rotate(4deg)} to{opacity:1;transform:rotate(4deg)} }
// // .scanner-screen {
// //   width:100%;height:100%;background:#08090F;
// //   display:flex;flex-direction:column;align-items:center;justify-content:center;gap:12px;padding:12px;
// // }
// // .vf { width:100px;height:100px;position:relative;background:rgba(108,99,255,0.04);border-radius:10px; }
// // .vc { position:absolute;width:18px;height:18px;border-color:var(--a1);border-style:solid; }
// // .vc.tl{top:0;left:0;border-width:2px 0 0 2px;border-radius:4px 0 0 0}
// // .vc.tr{top:0;right:0;border-width:2px 2px 0 0;border-radius:0 4px 0 0}
// // .vc.bl{bottom:0;left:0;border-width:0 0 2px 2px;border-radius:0 0 0 4px}
// // .vc.br{bottom:0;right:0;border-width:0 2px 2px 0;border-radius:0 0 4px 0}
// // .laser {
// //   position:absolute;left:4px;right:4px;height:1.5px;
// //   background:linear-gradient(90deg,transparent,var(--a1),transparent);
// //   box-shadow:0 0 8px var(--a1);
// //   animation:sweep 2.2s ease-in-out infinite 1.5s;
// // }
// // @keyframes sweep{0%{top:6px;opacity:0}8%{opacity:1}92%{opacity:1}100%{top:88px;opacity:0}}
// // .mini-qr {
// //   position:absolute;inset:12px;
// //   display:grid;grid-template-columns:repeat(6,1fr);grid-template-rows:repeat(6,1fr);gap:2px;
// // }
// // .qrc { border-radius:1px; }
// // .scan-lbl{font-family:var(--M);font-size:9px;color:rgba(108,99,255,0.5);letter-spacing:0.5px;text-align:center}
// // .p-banner{width:100%;height:110px;background:linear-gradient(160deg,#110D2A,#1A1040);position:relative;overflow:hidden}
// // .p-banner::after{content:'';position:absolute;inset:0;background:linear-gradient(160deg,rgba(108,99,255,0.4),rgba(0,212,170,0.15))}
// // .p-fade{position:absolute;bottom:0;left:0;right:0;height:50px;background:linear-gradient(transparent,#0D0E1A);z-index:1}
// // .p-avatar{
// //   position:absolute;bottom:-20px;left:14px;z-index:2;
// //   width:42px;height:42px;border-radius:50%;border:2px solid #0D0E1A;
// //   background:var(--grad2);
// //   display:flex;align-items:center;justify-content:center;
// //   font-family:var(--D);font-size:13px;font-weight:800;color:#070810;
// //   animation:pop .5s cubic-bezier(.34,1.56,.64,1) both 2.6s;
// // }
// // @keyframes pop{from{transform:scale(.3);opacity:0}to{transform:scale(1);opacity:1}}
// // .p-reveal{width:100%;height:100%;animation:cr .7s cubic-bezier(.22,1,.36,1) both 2.1s}
// // @keyframes cr{from{transform:translateY(36px);opacity:0}to{transform:translateY(0);opacity:1}}
// // .p-info{padding:27px 12px 10px}
// // .p-name{font-family:var(--D);font-size:14px;font-weight:700;color:var(--w);margin-bottom:2px}
// // .p-name em{font-style:normal;background:var(--grad2);-webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text}
// // .p-role{font-size:8px;color:var(--m2);letter-spacing:0.3px;margin-bottom:10px}
// // .p-tiles{display:grid;grid-template-columns:repeat(3,1fr);gap:4px;margin-bottom:8px}
// // .p-tile{
// //   background:rgba(255,255,255,0.04);border:0.5px solid rgba(255,255,255,0.08);
// //   border-radius:7px;padding:6px 5px;display:flex;flex-direction:column;gap:3px;
// //   animation:ti .3s ease both;
// // }
// // .p-tile:nth-child(1){animation-delay:2.7s}.p-tile:nth-child(2){animation-delay:2.85s}.p-tile:nth-child(3){animation-delay:3s}
// // @keyframes ti{from{opacity:0;transform:translateY(5px)}to{opacity:1;transform:translateY(0)}}
// // .p-tdot{width:10px;height:10px;border-radius:50%;background:linear-gradient(135deg,rgba(108,99,255,0.25),rgba(0,212,170,0.2));display:flex;align-items:center;justify-content:center}
// // .p-tdot::after{content:'';width:4px;height:4px;border-radius:50%;background:var(--a1)}
// // .p-tlbl{font-size:7px;color:var(--m2);font-weight:500}
// // .p-save{
// //   width:100%;padding:7px;border:none;border-radius:7px;
// //   background:var(--grad2);color:#070810;
// //   font-size:7.5px;font-weight:700;letter-spacing:0.3px;
// //   display:flex;align-items:center;justify-content:center;gap:3px;
// //   animation:ti .3s ease both 3.1s;
// // }
// // .beam-line{position:absolute;top:165px;left:148px;width:86px;height:1px;background:linear-gradient(90deg,var(--a1),transparent);z-index:5;animation:bm .4s ease both 1.7s}
// // @keyframes bm{from{transform:scaleX(0);transform-origin:left}to{transform:scaleX(1);transform-origin:left}}
// // .beam-dot{position:absolute;top:160px;left:148px;width:8px;height:8px;border-radius:50%;background:var(--a1);box-shadow:0 0 10px var(--a1);animation:bdot 2.2s ease-in-out infinite 1.8s;z-index:6}
// // @keyframes bdot{0%{left:148px;opacity:0;transform:scale(.5)}10%{opacity:1;transform:scale(1)}90%{opacity:.9}100%{left:226px;opacity:0;transform:scale(.5)}}

// // /* DIVIDER */
// // .lp-div{height:0.5px;background:linear-gradient(90deg,transparent,rgba(108,99,255,0.3),rgba(0,212,170,0.2),transparent)}

// // /* TRUST */
// // .lp-trust{
// //   display:flex;align-items:center;justify-content:center;gap:24px;flex-wrap:wrap;
// //   padding:20px 40px;background:rgba(255,255,255,0.015);
// // }
// // .lp-trust-lbl{font-family:var(--M);font-size:10px;color:var(--m2);letter-spacing:1.5px;text-transform:uppercase}
// // .lp-trust-items{display:flex;align-items:center;gap:20px;flex-wrap:wrap}
// // .lp-trust-item{display:flex;align-items:center;gap:6px;font-size:13px;font-weight:500;color:var(--m)}
// // .lp-trust-sep{color:rgba(108,99,255,0.35);font-size:14px}

// // /* SECTIONS */
// // .lp-sec{position:relative;z-index:1;padding:80px 40px}
// // .lp-inner{max-width:1140px;margin:0 auto}
// // .lp-eyebrow{
// //   display:block;text-align:center;
// //   font-family:var(--M);font-size:11px;font-weight:700;
// //   letter-spacing:2px;text-transform:uppercase;
// //   background:var(--grad);
// //   -webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text;
// //   margin-bottom:16px;
// // }
// // .lp-h2{
// //   font-family:var(--D);
// //   font-size:clamp(2.2rem,4.5vw,3.5rem);
// //   font-weight:800;line-height:1.06;letter-spacing:-1.2px;
// //   color:var(--w);text-align:center;margin-bottom:14px;
// // }
// // .lp-sub{
// //   font-size:15px;font-weight:400;color:var(--m);
// //   text-align:center;max-width:480px;margin:0 auto 48px;line-height:1.9;
// // }

// // /* STEPS */
// // .lp-steps{
// //   display:grid;grid-template-columns:repeat(3,1fr);
// //   gap:1px;background:var(--bd);border-radius:20px;overflow:hidden;
// // }
// // .lp-step{
// //   background:var(--s1);padding:36px 28px;
// //   position:relative;overflow:hidden;transition:background .3s;cursor:default;
// // }
// // .lp-step:hover{background:var(--s2)}
// // .lp-step::before{
// //   content:'';position:absolute;top:0;left:0;right:0;height:2px;
// //   background:var(--grad2);transform:scaleX(0);transform-origin:left;transition:transform .4s;
// // }
// // .lp-step:hover::before{transform:scaleX(1)}
// // .lp-step-num{
// //   position:absolute;top:20px;right:20px;
// //   font-family:var(--M);font-size:40px;font-weight:700;color:rgba(255,255,255,0.04);line-height:1;
// // }
// // .lp-step-icon{
// //   width:48px;height:48px;border-radius:14px;
// //   background:linear-gradient(135deg,rgba(108,99,255,0.15),rgba(0,212,170,0.1));
// //   border:0.5px solid rgba(108,99,255,0.25);
// //   display:flex;align-items:center;justify-content:center;
// //   font-size:22px;margin-bottom:20px;
// // }
// // .lp-step-title{font-family:var(--D);font-size:22px;font-weight:700;color:var(--w);margin-bottom:10px;letter-spacing:-0.3px}
// // .lp-step-desc{font-size:14px;color:var(--m);line-height:1.8}

// // /* FEATURES */
// // .lp-feat-grid{
// //   display:grid;grid-template-columns:repeat(4,1fr);
// //   gap:1px;background:var(--bd);border-radius:20px;overflow:hidden;
// // }
// // .lp-feat{background:var(--s1);padding:28px 22px;transition:background .25s}
// // .lp-feat:hover{background:var(--s2)}
// // .lp-feat:hover .lp-feat-icon{transform:scale(1.1) rotate(-5deg)}
// // .lp-feat-icon{font-size:26px;margin-bottom:16px;display:block;transition:transform .25s}
// // .lp-feat-title{font-family:var(--D);font-size:18px;font-weight:700;color:var(--w);margin-bottom:8px}
// // .lp-feat-desc{font-size:13px;color:var(--m);line-height:1.85}

// // /* USE CASES */
// // .lp-use-tabs{display:flex;flex-wrap:wrap;gap:8px;justify-content:center;margin-bottom:32px}
// // .lp-use-tab{
// //   padding:8px 16px;border-radius:100px;
// //   font-family:var(--B);font-size:13px;font-weight:500;
// //   cursor:pointer;transition:all .2s;
// //   border:0.5px solid rgba(255,255,255,0.08);
// //   background:transparent;color:var(--m);
// // }
// // .lp-use-tab.active{border-color:rgba(108,99,255,0.5);background:rgba(108,99,255,0.12);color:#B0A6FF}
// // .lp-use-panel{
// //   display:grid;grid-template-columns:1fr 1fr;gap:40px;align-items:center;
// //   background:var(--s1);border:0.5px solid rgba(108,99,255,0.2);
// //   border-radius:22px;padding:44px;position:relative;overflow:hidden;
// // }
// // .lp-use-panel::before{content:'';position:absolute;top:0;left:0;right:0;height:2px;background:var(--grad)}
// // .lp-use-badge{
// //   display:inline-flex;align-items:center;gap:8px;padding:5px 14px;border-radius:100px;
// //   margin-bottom:18px;background:rgba(108,99,255,0.1);color:#B0A6FF;
// //   border:0.5px solid rgba(108,99,255,0.25);font-size:12px;font-weight:600;
// // }
// // .lp-use-tagline{font-family:var(--D);font-size:30px;font-weight:800;color:var(--w);line-height:1.1;margin-bottom:12px;letter-spacing:-0.5px}
// // .lp-use-desc{font-size:14px;color:var(--m);line-height:1.9;margin-bottom:24px}
// // .lp-use-hls{display:flex;flex-direction:column;gap:10px}
// // .lp-hl{
// //   display:flex;align-items:center;gap:12px;
// //   background:var(--s2);border:0.5px solid var(--bd);
// //   border-radius:12px;padding:14px 16px;transition:border-color .2s;
// // }
// // .lp-hl:hover{border-color:rgba(108,99,255,0.3)}
// // .lp-hl-chk{
// //   width:26px;height:26px;border-radius:50%;flex-shrink:0;
// //   display:flex;align-items:center;justify-content:center;
// //   background:linear-gradient(135deg,rgba(108,99,255,0.2),rgba(0,212,170,0.15));
// //   border:0.5px solid rgba(0,212,170,0.25);
// //   font-size:11px;font-weight:700;color:var(--a2);
// // }
// // .lp-hl-txt{font-size:13px;font-weight:500;color:rgba(240,240,255,0.7)}

// // /* PRICING */
// // .lp-price-wrap{display:flex;justify-content:center}
// // .lp-price-card{
// //   background:var(--s1);border:0.5px solid rgba(108,99,255,0.3);
// //   border-radius:28px;padding:44px 40px;
// //   width:100%;max-width:440px;text-align:center;position:relative;overflow:hidden;
// // }
// // .lp-price-card::before{content:'';position:absolute;top:0;left:0;right:0;height:2px;background:var(--grad)}
// // .lp-price-card::after{
// //   content:'';position:absolute;top:-80px;left:50%;transform:translateX(-50%);
// //   width:280px;height:200px;
// //   background:radial-gradient(circle,rgba(108,99,255,0.12),transparent 70%);
// //   pointer-events:none;
// // }
// // .lp-price-badge{
// //   display:inline-block;
// //   background:var(--grad2);color:#070810;
// //   border-radius:100px;padding:5px 16px;
// //   font-size:11px;font-weight:700;letter-spacing:0.5px;text-transform:uppercase;
// //   margin-bottom:24px;position:relative;z-index:1;
// // }
// // .lp-price-num{
// //   font-family:var(--D);font-size:82px;font-weight:800;
// //   line-height:1;letter-spacing:-4px;
// //   background:var(--grad);
// //   -webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text;
// //   position:relative;z-index:1;
// // }
// // .lp-price-period{
// //   font-family:var(--M);font-size:11px;color:var(--m2);
// //   letter-spacing:2px;text-transform:uppercase;margin:8px 0 28px;
// //   position:relative;z-index:1;
// // }
// // .lp-price-list{list-style:none;text-align:left;margin-bottom:28px}
// // .lp-price-item{
// //   display:flex;align-items:center;gap:10px;
// //   padding:10px 0;font-size:14px;font-weight:500;color:rgba(240,240,255,0.72);
// //   border-bottom:0.5px solid var(--bd);
// // }
// // .lp-price-chk{background:var(--grad2);-webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text;font-size:13px;flex-shrink:0}

// // /* CTA */
// // .lp-cta{
// //   padding:80px 40px;text-align:center;
// //   position:relative;z-index:1;
// // }
// // .lp-cta::before{
// //   content:'';position:absolute;inset:0;
// //   background:radial-gradient(ellipse 80% 70% at 50% 50%,rgba(108,99,255,0.1),rgba(0,212,170,0.04) 40%,transparent 70%);
// //   pointer-events:none;
// // }
// // .lp-cta-title{
// //   font-family:var(--D);
// //   font-size:clamp(3rem,7vw,5.5rem);
// //   font-weight:800;line-height:1.0;letter-spacing:-2px;
// //   color:var(--w);margin-bottom:20px;
// // }
// // .lp-cta-sub{font-size:16px;color:var(--m);margin-bottom:36px;line-height:1.8}

// // /* FOOTER */
// // .lp-footer{padding:32px 40px 48px;border-top:0.5px solid var(--bd);position:relative;z-index:1}
// // .lp-footer-inner{
// //   max-width:1140px;margin:0 auto;
// //   display:flex;flex-direction:column;align-items:center;gap:12px;
// // }
// // .lp-footer-tag{font-size:13px;color:var(--m2)}
// // .lp-footer-copy{font-size:12px;color:rgba(240,240,255,0.15)}

// // /* REVEAL */
// // .reveal{opacity:0;transform:translateY(20px);transition:opacity .65s ease,transform .65s ease}
// // .reveal.visible{opacity:1;transform:translateY(0)}

// // /* LIGHT THEME */
// // .lp-light{
// //   --bg:#F4F5FB;--s1:#FFFFFF;--s2:#F0F1FA;
// //   --bd:rgba(0,0,0,0.07);--bd2:rgba(0,0,0,0.12);
// //   --w:#0A0B14;--m:rgba(10,11,20,0.55);--m2:rgba(10,11,20,0.35);
// //   --a1:#5249CC;--a2:#009980;--a3:#CC4480;
// //   --grad2:linear-gradient(135deg,#5249CC,#009980);
// //   --grad:linear-gradient(135deg,#5249CC,#009980,#CC4480);
// // }
// // .lp-light{background:var(--bg);color:var(--w)}
// // .lp-light .b1{background:rgba(82,73,204,0.1)}
// // .lp-light .b2{background:rgba(0,153,128,0.07)}
// // .lp-light .b3{background:rgba(204,68,128,0.06)}
// // .lp-light .lp-nav.scrolled{background:rgba(244,245,251,0.9)}
// // .lp-light .phone-a,.lp-light .phone-b{filter:none}
// // .lp-light .lp-trust{background:rgba(0,0,0,0.025)}
// // .lp-light .lp-use-panel,.lp-light .lp-price-card{background:var(--s1)}
// // .lp-light .lp-hl,.lp-light .lp-step,.lp-light .lp-feat{background:var(--s1)}
// // .lp-light .lp-step:hover,.lp-light .lp-feat:hover,.lp-light .lp-hl:hover{background:var(--s2)}
// // .lp-light .lp-btn-ghost{color:rgba(10,11,20,0.5);border-color:rgba(0,0,0,0.15)}
// // .lp-light .lp-btn-ghost:hover{color:var(--w);border-color:rgba(82,73,204,0.4)}

// // /* MOBILE */
// // @media(max-width:900px){
// //   .lp-hero{grid-template-columns:1fr;text-align:center;padding:100px 24px 60px}
// //   .lp-hero-sub,.lp-stats{margin-left:auto;margin-right:auto}
// //   .lp-ctas{justify-content:center}
// //   .lp-stats{justify-content:center}
// //   .lp-hero-right{margin-top:32px;display:flex;justify-content:center}
// //   .scene{transform:scale(0.82)}
// //   .lp-steps{grid-template-columns:1fr}
// //   .lp-feat-grid{grid-template-columns:1fr 1fr}
// //   .lp-use-panel{grid-template-columns:1fr;padding:28px}
// //   .lp-nav{padding:12px 24px}
// //   .lp-nav.scrolled{padding:10px 24px}
// //   .lp-sec{padding:60px 24px}
// //   .lp-use-tagline{font-size:24px}
// //   .lp-cta{padding:60px 24px}
// // }
// // @media(max-width:600px){
// //   .lp-feat-grid{grid-template-columns:1fr}
// //   .lp-h1{font-size:2.8rem}
// //   .lp-nav-link{display:none}
// // }
// // `;

// // const QR_PATTERN = [
// //     [1, 1, 1, 0, 1, 0], [1, 0, 1, 1, 0, 1], [1, 1, 1, 0, 1, 1],
// //     [0, 1, 0, 1, 0, 0], [1, 0, 1, 1, 1, 0], [0, 1, 0, 0, 1, 1],
// // ];

// // function ScanScene() {
// //     return (
// //         <div className="scene">
// //             <div className="phone-a">
// //                 <div className="scanner-screen">
// //                     <div className="vf">
// //                         <div className="vc tl" /><div className="vc tr" />
// //                         <div className="vc bl" /><div className="vc br" />
// //                         <div className="laser" />
// //                         <div className="mini-qr">
// //                             {QR_PATTERN.map((row, ri) => row.map((c, ci) => (
// //                                 <div key={`${ri}-${ci}`} className="qrc"
// //                                     style={{ background: c ? "rgba(108,99,255,0.6)" : "transparent" }} />
// //                             )))}
// //                         </div>
// //                     </div>
// //                     <div className="scan-lbl">SCAN TO CONNECT</div>
// //                 </div>
// //             </div>
// //             <div className="beam-line" />
// //             <div className="beam-dot" />
// //             <div className="phone-b">
// //                 <div className="p-reveal">
// //                     <div className="p-banner">
// //                         <div className="p-fade" />
// //                         <div className="p-avatar">UN</div>
// //                     </div>
// //                     <div className="p-info">
// //                         <div className="p-name">User <em>Name</em></div>
// //                         <div className="p-role">Director · Company Name</div>
// //                         <div className="p-tiles">
// //                             {["Phone", "Email", "Delhi"].map(l => (
// //                                 <div className="p-tile" key={l}>
// //                                     <div className="p-tdot" /><div className="p-tlbl">{l}</div>
// //                                 </div>
// //                             ))}
// //                         </div>
// //                         <button className="p-save">↓ Save Contact</button>
// //                         <div className="p-tiles" style={{ marginTop: "8px" }}>
// //                             {["Website", "Resume", "Social"].map((l, i) => (
// //                                 <div className="p-tile" key={l} style={{ animationDelay: `${3.2 + i * 0.15}s` }}>
// //                                     <div className="p-tdot" /><div className="p-tlbl">{l}</div>
// //                                 </div>
// //                             ))}
// //                         </div>
// //                     </div>
// //                 </div>
// //             </div>
// //         </div>
// //     );
// // }

// // const USE_CASES = [
// //     {
// //         icon: "👔", role: "Job Seekers", tagline: "Land your next opportunity faster",
// //         desc: "Walk into every interview with a digital card that includes your resume, LinkedIn, GitHub, and portfolio — all in one elegant scan.",
// //         hl: ["Resume attached & downloadable", "LinkedIn & GitHub links", "QR code for instant sharing", "Save contact in one tap"]
// //     },
// //     {
// //         icon: "💼", role: "Freelancers", tagline: "Win clients before you speak",
// //         desc: "Share everything a client needs — portfolio, social proof, and contact — in a single professional link. Close deals faster.",
// //         hl: ["Portfolio & website link", "WhatsApp quick contact", "Instagram & social proof", "Professional digital presence"]
// //     },
// //     {
// //         icon: "🏢", role: "Business Owners", tagline: "Network smarter, not harder",
// //         desc: "Stop spending on printed cards that get lost. Share via QR at events — always updated, never outdated.",
// //         hl: ["Always up-to-date details", "Google Wallet for offline use", "QR code for events & meetups", "Share via WhatsApp or email"]
// //     },
// //     {
// //         icon: "🎓", role: "Students", tagline: "Build your identity early",
// //         desc: "Stand out in campus placements. Show your skills, projects, and social profiles before you say a word.",
// //         hl: ["GitHub & project links", "Resume upload for placements", "LinkedIn profile showcase", "Free forever — no cost"]
// //     },
// //     {
// //         icon: "🧑‍💻", role: "Developers", tagline: "Your entire dev profile in one link",
// //         desc: "GitHub, LinkedIn, Twitter, personal site, resume — all in one clean card. Share at hackathons or open source communities.",
// //         hl: ["GitHub & portfolio links", "Twitter / X profile", "Resume for job applications", "Professional dev identity"]
// //     },
// // ];

// // function UseCases({ navigate }) {
// //     const [active, setActive] = useState(0);
// //     const cur = USE_CASES[active];
// //     return (
// //         <section className="lp-sec">
// //             <div className="lp-inner">
// //                 <span className="lp-eyebrow reveal">Who is it for</span>
// //                 <h2 className="lp-h2 reveal">Built for every <span className="gt">professional</span></h2>
// //                 <p className="lp-sub reveal">Whether you're a student or a CEO — your digital card works for you.</p>
// //                 <div className="lp-use-tabs reveal">
// //                     {USE_CASES.map((u, i) => (
// //                         <button key={i} className={`lp-use-tab${i === active ? " active" : ""}`} onClick={() => setActive(i)}>
// //                             {u.icon} {u.role}
// //                         </button>
// //                     ))}
// //                 </div>
// //                 <div className="lp-use-panel">
// //                     <div>
// //                         <div className="lp-use-badge"><span>{cur.icon}</span><span>{cur.role}</span></div>
// //                         <div className="lp-use-tagline">{cur.tagline}</div>
// //                         <p className="lp-use-desc">{cur.desc}</p>
// //                         <button className="lp-btn-primary" onClick={() => navigate("/create")}>
// //                             Create My Card — Free →
// //                         </button>
// //                     </div>
// //                     <div className="lp-use-hls">
// //                         {cur.hl.map((h, i) => (
// //                             <div className="lp-hl" key={i}>
// //                                 <div className="lp-hl-chk">✓</div>
// //                                 <span className="lp-hl-txt">{h}</span>
// //                             </div>
// //                         ))}
// //                     </div>
// //                 </div>
// //             </div>
// //         </section>
// //     );
// // }

// // export default function Viewtwo() {
// //     const navigate = useNavigate();
// //     const [scrolled, setScrolled] = useState(false);
// //     const [isLight, setIsLight] = useState(false);

// //     useEffect(() => {
// //         const fn = () => setScrolled(window.scrollY > 40);
// //         window.addEventListener("scroll", fn);
// //         return () => window.removeEventListener("scroll", fn);
// //     }, []);

// //     useEffect(() => {
// //         const els = document.querySelectorAll(".reveal");
// //         const obs = new IntersectionObserver(
// //             e => e.forEach(x => { if (x.isIntersecting) x.target.classList.add("visible") }),
// //             { threshold: 0.1 }
// //         );
// //         els.forEach(el => obs.observe(el));
// //         return () => obs.disconnect();
// //     }, []);

// //     const ThemeIcon = () => isLight ? (
// //         <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={isLight ? "#5249CC" : "#6C63FF"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
// //             <circle cx="12" cy="12" r="5" />
// //             <line x1="12" y1="1" x2="12" y2="3" /><line x1="12" y1="21" x2="12" y2="23" />
// //             <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" /><line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
// //             <line x1="1" y1="12" x2="3" y2="12" /><line x1="21" y1="12" x2="23" y2="12" />
// //             <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" /><line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
// //         </svg>
// //     ) : (
// //         <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#6C63FF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
// //             <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
// //         </svg>
// //     );

// //     return (
// //         <div className={isLight ? "lp-light" : ""} style={{ background: isLight ? "#F4F5FB" : "#070810", minHeight: "100vh" }}>
// //             <style dangerouslySetInnerHTML={{ __html: GLOBAL_CSS }} />
// //             <div className="blob b1" /><div className="blob b2" /><div className="blob b3" />

// //             {/* NAV */}
// //             <nav className={`lp-nav${scrolled ? " scrolled" : ""}`}>
// //                 <div className="lp-nav-inner">
// //                     <a href="/" className="lp-logo">
// //                         <div className="lp-logo-mark">M</div>
// //                         <span className="lp-logo-text">myprofile<strong>corner</strong></span>
// //                     </a>
// //                     <div className="lp-nav-links">
// //                         <a href="#how" className="lp-nav-link">How it works</a>
// //                         <a href="#features" className="lp-nav-link">Features</a>
// //                         <a href="#pricing" className="lp-nav-link">Pricing</a>
// //                         <button className="lp-theme-btn" onClick={() => setIsLight(p => !p)} aria-label="Toggle theme">
// //                             <ThemeIcon />
// //                         </button>
// //                         <button className="lp-nav-cta" onClick={() => navigate("/create")}>Create Free Card</button>
// //                     </div>
// //                 </div>
// //             </nav>

// //             {/* HERO */}
// //             <div className="lp-hero-wrap">
// //                 <div className="lp-hero">
// //                     <div>
// //                         <div className="lp-pill">
// //                             <div className="lp-pill-dot" />
// //                             <span className="lp-pill-text">100% FREE · NO APP NEEDED</span>
// //                         </div>
// //                         <h1 className="lp-h1">
// //                             Your Digital<br />
// //                             Identity,<br />
// //                             <span className="line-grad">Always Ready.</span>
// //                         </h1>
// //                         <p className="lp-hero-sub">
// //                             Replace paper visiting cards with a smart digital card. Share your profile
// //                             instantly via QR code, link, or Google Wallet — no printing, no waste.
// //                         </p>
// //                         <div className="lp-ctas">
// //                             <button className="lp-btn-primary" onClick={() => navigate("/create")}>
// //                                 Create My Card — It's Free →
// //                             </button>
// //                             <a href="#how" className="lp-btn-ghost">See how it works</a>
// //                         </div>
// //                         <div className="lp-stats">
// //                             {[["100%", "Free forever"], ["2 min", "Setup time"], ["0", "Paper waste"]].map(([n, l]) => (
// //                                 <div key={l}>
// //                                     <div className="lp-stat-n">{n}</div>
// //                                     <div className="lp-stat-l">{l}</div>
// //                                 </div>
// //                             ))}
// //                         </div>
// //                     </div>
// //                     <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
// //                         <ScanScene />
// //                     </div>
// //                 </div>
// //             </div>

// //             <div className="lp-div" />

// //             {/* TRUST */}
// //             <div className="lp-trust">
// //                 <span className="lp-trust-lbl">Works seamlessly with</span>
// //                 <div className="lp-trust-items">
// //                     {[["🔥", "Firebase"], ["🪙", "Google Wallet"], ["📱", "Android & iOS"], ["🔗", "Any Browser"]].map(([icon, label], i, arr) => (
// //                         <React.Fragment key={label}>
// //                             <div className="lp-trust-item"><span>{icon}</span><span>{label}</span></div>
// //                             {i < arr.length - 1 && <span className="lp-trust-sep">·</span>}
// //                         </React.Fragment>
// //                     ))}
// //                 </div>
// //             </div>

// //             <div className="lp-div" />

// //             {/* HOW IT WORKS */}
// //             <section id="how" className="lp-sec">
// //                 <div className="lp-inner">
// //                     <span className="lp-eyebrow reveal">How it works</span>
// //                     <h2 className="lp-h2 reveal">From setup to sharing <span className="gt">in 3 steps</span></h2>
// //                     <p className="lp-sub reveal">No app downloads. No tech skills required. Just fill, share, impress.</p>
// //                     <div className="lp-steps reveal">
// //                         {[
// //                             { num: "01", icon: "✏️", title: "Fill your profile", desc: "Add your name, photo, contact details, social links, resume, and website. Takes less than 2 minutes." },
// //                             { num: "02", icon: "🎴", title: "Get your digital card", desc: "Instantly receive a beautiful card with a unique QR code and shareable link — no design skills needed." },
// //                             { num: "03", icon: "🚀", title: "Share anywhere", desc: "Show your QR in meetings, add to Google Wallet, or send your link on WhatsApp, email, or LinkedIn." },
// //                         ].map(s => (
// //                             <div className="lp-step" key={s.num}>
// //                                 <div className="lp-step-num">{s.num}</div>
// //                                 <div className="lp-step-icon">{s.icon}</div>
// //                                 <div className="lp-step-title">{s.title}</div>
// //                                 <p className="lp-step-desc">{s.desc}</p>
// //                             </div>
// //                         ))}
// //                     </div>
// //                 </div>
// //             </section>

// //             <div className="lp-div" />

// //             {/* FEATURES */}
// //             <section id="features" className="lp-sec">
// //                 <div className="lp-inner">
// //                     <span className="lp-eyebrow reveal">Features</span>
// //                     <h2 className="lp-h2 reveal">Everything a modern <span className="gt">professional</span> needs</h2>
// //                     <div className="lp-feat-grid reveal">
// //                         {[
// //                             { icon: "📸", title: "Profile Photo", desc: "Upload your photo — it appears on your card and in your Google Wallet pass." },
// //                             { icon: "🔗", title: "All Social Links", desc: "LinkedIn, GitHub, Instagram, Twitter, WhatsApp, YouTube — show what matters." },
// //                             { icon: "📄", title: "Resume Attachment", desc: "Attach your PDF resume. Recruiters can download it instantly." },
// //                             { icon: "🪙", title: "Google Wallet", desc: "Add your card to Google Wallet and show it offline anytime, anywhere." },
// //                             { icon: "📲", title: "QR Code", desc: "Every card gets a unique QR code. One scan opens your full profile instantly." },
// //                             { icon: "💾", title: "Save Contact", desc: "Visitors can save your details directly to their phone contacts with one tap." },
// //                             { icon: "✏️", title: "Edit Anytime", desc: "Changed jobs? New number? Update instantly — the link stays the same." },
// //                             { icon: "🌐", title: "Works Everywhere", desc: "Opens in any browser on any device. No app install required." },
// //                         ].map(f => (
// //                             <div className="lp-feat" key={f.title}>
// //                                 <span className="lp-feat-icon">{f.icon}</span>
// //                                 <div className="lp-feat-title">{f.title}</div>
// //                                 <p className="lp-feat-desc">{f.desc}</p>
// //                             </div>
// //                         ))}
// //                     </div>
// //                 </div>
// //             </section>

// //             <div className="lp-div" />

// //             <UseCases navigate={navigate} />

// //             <div className="lp-div" />

// //             {/* PRICING */}
// //             <section id="pricing" className="lp-sec">
// //                 <div className="lp-inner">
// //                     <span className="lp-eyebrow reveal">Pricing</span>
// //                     <h2 className="lp-h2 reveal">Simple, <span className="gt">honest</span> pricing</h2>
// //                     <p className="lp-sub reveal">No hidden charges. No credit card needed.</p>
// //                     <div className="lp-price-wrap reveal">
// //                         <div className="lp-price-card">
// //                             <div className="lp-price-badge">Most Popular</div>
// //                             <div className="lp-price-num">₹0</div>
// //                             <div className="lp-price-period">Forever Free</div>
// //                             <ul className="lp-price-list">
// //                                 {["1 Digital Visiting Card", "Unique QR Code & Link", "Profile Photo Upload",
// //                                     "Resume Upload", "All Social Media Links", "Google Wallet Pass",
// //                                     "Save Contact (.vcf)", "Edit Anytime", "No App Required"].map(item => (
// //                                         <li className="lp-price-item" key={item}>
// //                                             <span className="lp-price-chk">✦</span>{item}
// //                                         </li>
// //                                     ))}
// //                             </ul>
// //                             <button className="lp-btn-primary" style={{ width: "100%", justifyContent: "center" }}
// //                                 onClick={() => navigate("/create")}>
// //                                 Get Started Free →
// //                             </button>
// //                         </div>
// //                     </div>
// //                 </div>
// //             </section>

// //             <div className="lp-div" />

// //             {/* CTA */}
// //             <div className="lp-cta">
// //                 <h2 className="lp-cta-title reveal">
// //                     Stop carrying<br />
// //                     <span className="gt3">paper cards.</span>
// //                 </h2>
// //                 <p className="lp-cta-sub reveal">Create your digital card in under 2 minutes. Free, forever.</p>
// //                 <button className="lp-btn-primary reveal" style={{ margin: "0 auto" }}
// //                     onClick={() => navigate("/create")}>
// //                     Create My Card Now →
// //                 </button>
// //             </div>

// //             <div className="lp-div" />

// //             {/* FOOTER */}
// //             <footer className="lp-footer">
// //                 <div className="lp-footer-inner">
// //                     <a href="/" className="lp-logo" style={{ textDecoration: "none" }}>
// //                         <div className="lp-logo-mark">M</div>
// //                         <span className="lp-logo-text">myprofile<strong>corner</strong></span>
// //                     </a>
// //                     <p className="lp-footer-tag">Your digital identity, always in your pocket.</p>
// //                     <p className="lp-footer-copy">© {new Date().getFullYear()} MyProfileCorner · Made with ♥ in India</p>
// //                 </div>
// //             </footer>
// //         </div>
// //     );
// // }
